import * as THREE from 'three';
import type {Subscription} from 'rxjs';
import {
    isDashDotLIneNodeMaterial,
    LineMaterialFactory,
    LineMaterialState,
    PointsMaterialFactory,
    SketchEdgeBaseStyle,
} from '../../material';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    SketchArcObject,
    SketchCircleObject,
    SketchCollectionObject,
    SketchEllipseControlPointFactory,
    SketchEllipseObject,
    SketchLineObject,
    SketchParabolaObject,
    SketchPointKind,
    SketchPointObject,
    SketchBezierCurveClosureKind,
    SketchBezierCurveKind,
    SketchSplineObject,
    SketchStyleSplineObject,
} from '../../mesh/sketch';
import type {CollectSource} from '../../mesh/sketch';
import {isSketchContour} from '../../mesh/meshProxy/SketchContour';
import {
    EntityInteractionEventType,
    type SelectionUpdatedEventPayload,
} from '../../manager/EntitySelectionManager';
import {
    RawPointerEventType,
    type RawPointerEventPayload,
} from '../../viewer/moudles/EventManager';
import type {Viewer} from '../../viewer/Viewer';
import {ScreenSpacePrecisionPicker} from '../../manager/pick';
import {SketchTrimGeometryService} from './SketchTrimGeometryService';
import {
    SketchTrimCurveKind,
    SketchTrimMode,
    SketchTrimSingleEntityBehavior,
    SketchTrimControllerStage,
    SketchTrimEndpointRole,
    SketchTrimExtendSide,
    type SketchTrimCurveData,
    type SketchTrimCurveObject,
    type SketchTrimEntityRef,
    type SketchTrimOptions,
    type SketchTrimControllerSnapshot,
    type SketchTrimLineData,
    type SketchTrimOperationResult,
    type SketchTrimPowerMarkerSnapshot,
    type SketchTrimPowerUndoRecord,
    type SketchTrimLineSnapshot,
    type SketchTrimRemovedLineSnapshot,
    type SketchTrimUpdatedCurveSnapshot,
    SketchTrimRemovedLineParentKind,
    type SketchTrimPickResult,
    type SketchTrimSegmentRange,
} from './SketchTrimTypes';
import {
    type SketchTrimModeService,
    SketchTrimPowerModeService,
    SketchTrimCornerModeService,
    SketchTrimToClosestModeService,
    SketchTrimAwayInsideModeService,
    SketchTrimAwayOutsideModeService,
} from './modeServices';
import {SketchTrimSlotTopologyService} from './SketchTrimSlotTopologyService';
import {SketchTrimLineEndpointTransferService} from './SketchTrimLineEndpointTransferService';
import {SketchTrimConstraintCleanupService} from './SketchTrimConstraintCleanupService';
import {SketchTrimDimensionIntegrationService} from './SketchTrimDimensionIntegrationService';
import {SketchTrimCornerEndpointTopologyService} from './SketchTrimCornerEndpointTopologyService';
import {SketchTrimCornerGeometryService} from './SketchTrimCornerGeometryService';
import type {EntityPatch} from '../../history/types';
import type {SketchDimensionKind} from '../dimension/SketchDimensionTypes';
import type {SketchDimensionTopologyChange} from '../../../types/sketch/dimension/SketchDimensionTopologyChangeTypes';
import type {
    SketchTrimCurveIdentitySnapshot,
    SketchTrimCurveRemovalResult,
    SketchTrimDimensionStateSnapshot,
} from '../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';
import {SketchEllipseParametricGeometryService} from '../geometry/SketchEllipseParametricGeometryService';

/**
 * 草图剪裁命令枚举。
 * 说明：
 * 1. 仅收敛当前工具栏已开放的剪裁族命令；
 * 2. 通过枚举约束命令分支，避免散落字符串。
 */
export enum SketchTrimCommand {
    /** 剪裁实体命令。 */
    TrimEntities = 'sketch-trim-entities',
    /** 延伸实体命令。 */
    ExtendEntities = 'sketch-extend-entities',
}

/**
 * 剪裁命令配置。
 */
export interface SketchTrimCommandConfig {
    /** 属性面板显示模式。 */
    mode: SketchTrimMode;
    /** 工具栏状态栏显示名称。 */
    name: string;
    /** 工具栏状态栏显示提示。 */
    hint: string;
}

/**
 * 剪裁命令到配置的映射表。
 */
export const SKETCH_TRIM_COMMAND_CONFIG: Readonly<Record<SketchTrimCommand, SketchTrimCommandConfig>> = {
    [SketchTrimCommand.TrimEntities]: {
        mode: SketchTrimMode.Power,
        name: '剪裁实体',
        hint: '强劲剪裁：左键拖动连续修剪，按住 Shift 在直线、圆弧或部分椭圆上拖动可沿自然路径延伸，回拖红方块可撤销一步',
    },
    [SketchTrimCommand.ExtendEntities]: {
        mode: SketchTrimMode.TrimToClosest,
        name: '延伸实体',
        hint: '延伸实体：选择直线靠近待延伸的一侧，延伸到最近边界',
    },
};

/**
 * 剪裁选项默认值。
 */
export const DEFAULT_SKETCH_TRIM_OPTIONS: SketchTrimOptions = {
    mode: SketchTrimMode.Power,
    singleEntityBehavior: SketchTrimSingleEntityBehavior.Trim,
    keepTrimmedAsConstruction: false,
    ignoreConstructionGeometry: false,
};

/**
 * 判断是否为剪裁族命令。
 * @param commandType 命令字符串。
 * @returns 是否为剪裁族命令。
 */
export function isSketchTrimCommand(commandType: string): commandType is SketchTrimCommand {
    return commandType === SketchTrimCommand.TrimEntities || commandType === SketchTrimCommand.ExtendEntities;
}

/**
 * 解析剪裁命令配置。
 * @param commandType 剪裁命令。
 * @returns 剪裁命令配置。
 */
export function resolveSketchTrimCommandConfig(commandType: SketchTrimCommand): SketchTrimCommandConfig {
    return SKETCH_TRIM_COMMAND_CONFIG[commandType];
}

/**
 * 草图剪裁控制器。
 * 职责：
 * 1. 管理剪裁模式生命周期；
 * 2. 监听鼠标交互并执行剪裁/延伸；
 * 3. 负责线段拆分、删除与构造线保留；
 * 4. 作为五种模式服务的统一编排层。
 */
export class SketchTrimController {
    /** 默认剪裁选项。 */
    public static readonly DEFAULT_OPTIONS: SketchTrimOptions = {
        ...DEFAULT_SKETCH_TRIM_OPTIONS,
    };

    /** 线拾取阈值。 */
    private static readonly LINE_PICK_THRESHOLD = 0.5;
    /** Trim 交点调试精灵尺寸。 */
    private static readonly DEBUG_INTERSECTION_SPRITE_SIZE = 1;

    /** 运行 Viewer。 */
    private readonly viewer: Viewer;
    /** 几何服务。 */
    private readonly geometryService: SketchTrimGeometryService;
    /** 权威椭圆参数几何服务。 */
    private readonly ellipseGeometryService: SketchEllipseParametricGeometryService;
    /** 槽口拓扑服务。 */
    private readonly slotTopologyService: SketchTrimSlotTopologyService;
    /** 线端点迁移服务。 */
    private readonly lineEndpointTransferService: SketchTrimLineEndpointTransferService;
    /** Corner 端点拓扑服务。 */
    private readonly cornerEndpointTopologyService: SketchTrimCornerEndpointTopologyService;
    /** 开放曲线 Corner 几何服务。 */
    private readonly cornerGeometryService: SketchTrimCornerGeometryService;
    /** 强力裁剪约束清理服务。 */
    private readonly constraintCleanupService: SketchTrimConstraintCleanupService;
    /** Trim 与智能尺寸领域集成服务。 */
    private readonly dimensionIntegrationService: SketchTrimDimensionIntegrationService;
    /** 射线器。 */
    private readonly raycaster: THREE.Raycaster = new THREE.Raycaster();
    /** 与草图选择共用像素拾取规则，避免同平面线命中退回场景顺序。 */
    private readonly screenSpacePicker = new ScreenSpacePrecisionPicker();
    /** 鼠标 NDC。 */
    private readonly mouseNdc: THREE.Vector2 = new THREE.Vector2();
    /** 原始事件订阅集合。 */
    private readonly subscriptions: Subscription[] = [];
    /** Trim 交点调试精灵。 */
    private readonly debugIntersectionSprites: THREE.Sprite[] = [];

    /** 当前阶段。 */
    private stage: SketchTrimControllerStage = SketchTrimControllerStage.Idle;
    /** 当前模式。 */
    private mode: SketchTrimMode = SketchTrimMode.Power;
    /** 当前选项。 */
    private options: SketchTrimOptions = {...SketchTrimController.DEFAULT_OPTIONS};
    /** 最近一次命中的实体。 */
    private lastSelectedEntity: SketchTrimEntityRef | null = null;
    /** 裁剪选择同步抑制标记。 */
    private suppressTrimSelectionSync = false;
    /** 模式服务映射表。 */
    private readonly modeServices: Readonly<Record<SketchTrimMode, SketchTrimModeService>>;

    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        if (!viewer.eventManager) {
            console.error(`[SketchTrimController] 剪裁控制器初始化失败：EventManager 不存在。`);
        }
        this.viewer = viewer;
        this.ellipseGeometryService = new SketchEllipseParametricGeometryService();
        this.geometryService = new SketchTrimGeometryService(this.ellipseGeometryService);
        this.cornerGeometryService = new SketchTrimCornerGeometryService(this.geometryService);
        this.slotTopologyService = new SketchTrimSlotTopologyService(viewer);
        this.lineEndpointTransferService = new SketchTrimLineEndpointTransferService();
        this.cornerEndpointTopologyService = new SketchTrimCornerEndpointTopologyService(
            (position, referenceLine) => this.createControlPoint(position, referenceLine),
            (point, referenceLine) => {
                if (referenceLine.parent) {
                    this.viewer.addObject(point, referenceLine.parent);
                    return;
                }
                this.addObjectToSketchGroup(point);
            },
            (point) => this.syncMaterialSnapshot(point),
        );
        this.constraintCleanupService = new SketchTrimConstraintCleanupService();
        this.dimensionIntegrationService = new SketchTrimDimensionIntegrationService(viewer);
        this.modeServices = {
            [SketchTrimMode.Power]: new SketchTrimPowerModeService(this),
            [SketchTrimMode.Corner]: new SketchTrimCornerModeService(this),
            [SketchTrimMode.TrimAwayInside]: new SketchTrimAwayInsideModeService(this),
            [SketchTrimMode.TrimAwayOutside]: new SketchTrimAwayOutsideModeService(this),
            [SketchTrimMode.TrimToClosest]: new SketchTrimToClosestModeService(this),
        };
    }

    /**
     * 启动剪裁模式。
     * @param mode 剪裁模式。
     * @param options 可选参数增量。
     */
    public startMode(mode: SketchTrimMode, options: Partial<SketchTrimOptions> = {}): void {
        this.mode = mode;
        this.options = {
            ...this.options,
            ...options,
            mode,
        };
        this.configureTrimSelectionPredicate();

        this.resetTransientState();
        this.bindPointerEvents();
        this.stage = SketchTrimControllerStage.Armed;

        this.viewer.drawingNoticeCallback?.(
            `已进入剪裁实体：${this.resolveModeDisplayName(this.mode)}`,
            'info',
        );
    }

    /**
     * 清理剪裁模式。
     * @param keepMode 是否保留当前模式值。
     */
    public clearMode(keepMode = false): void {
        const wasModeActive = this.stage !== SketchTrimControllerStage.Idle;
        this.unbindPointerEvents();
        /**
         * 空闲控制器没有裁剪选择的所有权。通用命令切换会幂等调用该方法，
         * 若此时清空全局选择，会提前删除草图入口、特征入口正在消费的预选。
         */
        if (wasModeActive) {
            this.clearTrimSelectionFromSelectionManager();
        }
        this.debugClearAllTrimIntersectionBoxes();
        this.resetTransientState();
        this.stage = SketchTrimControllerStage.Idle;

        if (!keepMode) {
            this.mode = SketchTrimMode.Power;
            this.options = {...SketchTrimController.DEFAULT_OPTIONS};
        }
        this.configureTrimSelectionPredicate();
    }

    /**
     * 释放控制器。
     */
    public dispose(): void {
        this.clearMode(false);
    }

    /**
     * 调试显示当前 Trim 候选曲线的全部交点。
     * @returns 创建的调试精灵数量。
     */
    public debugShowAllTrimIntersections(): number {
        this.debugClearAllTrimIntersectionBoxes();

        const curveCandidates = this.collectPowerTrimCurveCandidates(this.options.ignoreConstructionGeometry);
        const curveDataList = curveCandidates.map((candidate) => this.geometryService.resolveCurveData(candidate));
        const intersectionPoints = this.collectAllTrimIntersectionPoints(curveDataList);

        intersectionPoints.forEach((point) => {
            this.createDebugIntersectionSprite(point);
        });

        if (intersectionPoints.length > 0) {
            this.viewer.requestRender();
        }

        return intersectionPoints.length;
    }

    /**
     * 清理当前 Trim 调试交点精灵。
     */
    public debugClearAllTrimIntersectionBoxes(): void {
        this.debugIntersectionSprites.forEach((sprite) => {
            this.viewer.scene.remove(sprite);
            if (sprite.material instanceof THREE.Material) {
                sprite.material.dispose();
            }
        });
        this.debugIntersectionSprites.length = 0;
        this.viewer.requestRender();
    }

    /**
     * 判断当前是否激活剪裁模式。
     * @returns 是否激活。
     */
    public isModeActive(): boolean {
        return this.stage !== SketchTrimControllerStage.Idle;
    }

    /**
     * 更新剪裁模式。
     * @param mode 目标模式。
     */
    public setMode(mode: SketchTrimMode): void {
        if (!this.isModeActive()) {
            this.startMode(mode, {
                ...this.options,
                mode,
            });
            return;
        }

        this.mode = mode;
        this.options = {
            ...this.options,
            mode,
        };
        this.configureTrimSelectionPredicate();
        this.resetSelectionStateByMode();
        if (mode !== SketchTrimMode.Power) {
            this.clearPowerUndoState();
        }

        this.viewer.drawingNoticeCallback?.(
            `剪裁模式已切换：${this.resolveModeDisplayName(this.mode)}`,
            'info',
        );
    }

    /**
     * 更新剪裁选项。
     * @param options 选项增量。
     */
    public setOptions(options: Partial<SketchTrimOptions>): void {
        const nextMode = options.mode ?? this.mode;
        this.options = {
            ...this.options,
            ...options,
            mode: nextMode,
        };

        if (this.mode !== nextMode) {
            this.mode = nextMode;
            this.configureTrimSelectionPredicate();
            this.resetSelectionStateByMode();
        }
        if (nextMode !== SketchTrimMode.Power) {
            this.clearPowerUndoState();
        }
    }

    /**
     * 获取控制器快照。
     * @returns 快照对象。
     */
    public getSnapshot(): SketchTrimControllerSnapshot {
        return {
            mode: this.mode,
            options: {...this.options},
            stage: this.stage,
        };
    }

    /**
     * 获取最近一次命中的实体快照。
     * @returns 实体引用。
     */
    public getLastSelectedEntitySnapshot(): SketchTrimEntityRef | null {
        if (!this.lastSelectedEntity) {
            return null;
        }
        return {
            ...this.lastSelectedEntity,
            hitPoint: this.lastSelectedEntity.hitPoint
                ? [...this.lastSelectedEntity.hitPoint] as [number, number, number]
                : undefined,
        };
    }

    //------ 强力剪裁撤销能力 开始------
    /**
     * 获取当前可显示的强力剪裁标记快照。
     * @returns 可显示的剪裁标记；Shift、延伸记录或无标记时返回 null。
     */
    public getLastPowerTrimMarkerSnapshot(): SketchTrimPowerMarkerSnapshot | null {
        return this.getPowerModeService().getLastPowerTrimMarkerSnapshot();
    }

    /**
     * 撤销最近一次强力剪裁操作。
     * @returns 是否成功撤销。
     */
    public undoLastPowerTrim(): boolean {
        return this.getPowerModeService().undoLastPowerTrim();
    }
    //------ 强力剪裁撤销能力 结束------

    /**
     * 绑定鼠标事件。
     */
    private bindPointerEvents(): void {
        if (this.subscriptions.length > 0) {
            return;
        }

        const eventManager = this.viewer.eventManager;
        if (!eventManager) {
            console.error(`[SketchTrimController] 剪裁控制器绑定事件失败：EventManager 不存在。`);
            return;
        }

        this.subscriptions.push(
            eventManager.observe(RawPointerEventType.MouseDown).subscribe(this.handleMouseDown),
            eventManager.observe(RawPointerEventType.MouseMove).subscribe(this.handleMouseMove),
            eventManager.observe(RawPointerEventType.MouseUp).subscribe(this.handleMouseUp),
            eventManager.observe(RawPointerEventType.Click).subscribe(this.handleClick),
            eventManager.observe(RawPointerEventType.PointerCancel).subscribe(() => {
                // 浏览器失去指针时取消整次 Power 拖拽，不能等下一次 mousemove 才清理。
                if (this.isModeActive() && this.mode === SketchTrimMode.Power) {
                    this.getPowerModeService().handlePointerCancel();
                }
            }),
        );

        if (this.viewer.entitySelectionManager) {
            this.subscriptions.push(
                this.viewer.entitySelectionManager.observe(EntityInteractionEventType.SelectionUpdated)
                    .subscribe(this.handleTrimSelectionUpdated),
            );
        }
    }

    /**
     * 解绑鼠标事件。
     */
    private unbindPointerEvents(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /**
     * 鼠标按下处理器。
     * @param payload 原始事件。
     */
    private readonly handleMouseDown = (payload: RawPointerEventPayload): void => {
        if (!this.isModeActive()) {
            return;
        }
        this.getCurrentModeService().handleMouseDown?.(payload);
    };

    /**
     * 鼠标移动处理器。
     * @param payload 原始事件。
     */
    private readonly handleMouseMove = (payload: RawPointerEventPayload): void => {
        if (!this.isModeActive()) {
            return;
        }
        this.getCurrentModeService().handleMouseMove?.(payload);
    };

    /**
     * 鼠标抬起处理器。
     * @param payload 原始事件。
     */
    private readonly handleMouseUp = (payload: RawPointerEventPayload): void => {
        if (!this.isModeActive()) {
            return;
        }
        this.getCurrentModeService().handleMouseUp?.(payload);
    };

    /**
     * 鼠标点击处理器。
     * @param payload 原始事件。
     */
    private readonly handleClick = (payload: RawPointerEventPayload): void => {
        if (!this.isModeActive()) {
            return;
        }
        this.getCurrentModeService().handleClick?.(payload);
    };

    /**
     * 处理裁剪模式下的选择结算。
     * @param payload 选择结算事件。
     */
    private readonly handleTrimSelectionUpdated = (payload: SelectionUpdatedEventPayload): void => {
        void payload;
        if (!this.isModeActive()) {
            return;
        }
        if (this.suppressTrimSelectionSync) {
            return;
        }
        this.getCurrentModeService().handleSelectionUpdated?.();
    };

    /**
     * 获取当前模式服务。
     * @returns 模式服务。
     */
    private getCurrentModeService(): SketchTrimModeService {
        return this.modeServices[this.mode];
    }

    /**
     * 将当前裁剪子模式的实体准入规则注入统一选择管理器。
     *
     * 准入规则由具体模式服务提供时，控制器只负责注入，不解释该模式当前处于边界还是目标阶段。
     * Corner 使用开放曲线规则；TrimToClosest 由模式服务按剪裁/延伸行为注入准入规则。
     */
    private configureTrimSelectionPredicate(): void {
        const selectionManager = this.viewer.entitySelectionManager;
        if (!selectionManager || typeof selectionManager.setSketchTrimSelectableEntityPredicate !== 'function') {
            return;
        }
        const modeService = this.getCurrentModeService();
        selectionManager.setSketchTrimSelectableEntityPredicate(
            modeService.isSelectableEntity
                ? (object) => modeService.isSelectableEntity?.(object) ?? false
                : this.mode === SketchTrimMode.Corner
                ? (object) => this.cornerGeometryService.isSupportedOpenCurveObject(object)
                : isSketchLineObject,
        );
    }

    /**
     * 获取强力剪裁服务。
     * @returns 强力剪裁服务。
     */
    private getPowerModeService(): SketchTrimPowerModeService {
        return this.modeServices[SketchTrimMode.Power] as SketchTrimPowerModeService;
    }

    /**
     * 获取 Viewer。
     * @returns Viewer 实例。
     */
    public getViewer(): Viewer {
        return this.viewer;
    }

    /**
     * 捕获当前活动草图的智能尺寸状态。
     *
     * @returns 尺寸状态；当前没有尺寸领域上下文时返回 null。
     */
    public serviceCaptureActiveSketchDimensionState(): SketchTrimDimensionStateSnapshot | null {
        return this.dimensionIntegrationService.captureActiveState();
    }

    /**
     * 应用裁剪产生的持久拓扑变化并刷新尺寸关系视图。
     *
     * @param changes 持久拓扑变化列表。
     */
    public serviceApplyDimensionTopologyChanges(changes: readonly SketchDimensionTopologyChange[]): void {
        this.dimensionIntegrationService.applyTopologyChanges(changes);
    }

    /**
     * 创建尺寸 before/after 历史补丁。
     *
     * @param before 变更前尺寸状态。
     * @param after 变更后尺寸状态。
     * @returns 尺寸历史补丁。
     */
    public serviceCreateSketchDimensionHistoryPatches(
        before: SketchTrimDimensionStateSnapshot | null,
        after: SketchTrimDimensionStateSnapshot | null,
    ): EntityPatch[] {
        return this.dimensionIntegrationService.createHistoryPatches(before, after);
    }

    /**
     * 当前尺寸状态仍等于 expected 时恢复 target。
     *
     * @param expected 允许恢复的当前状态。
     * @param target 目标状态。
     */
    public serviceRestoreSketchDimensionStateIfCurrent(
        expected: SketchTrimDimensionStateSnapshot | null,
        target: SketchTrimDimensionStateSnapshot | null,
    ): void {
        this.dimensionIntegrationService.restoreStateIfCurrent(expected, target);
    }

    /**
     * 无条件恢复智能尺寸状态。
     *
     * @param state 目标状态。
     */
    public serviceRestoreSketchDimensionState(state: SketchTrimDimensionStateSnapshot | null): void {
        this.dimensionIntegrationService.restoreState(state);
    }

    /**
     * 获取当前阶段。
     * @returns 阶段。
     */
    public serviceGetStage(): SketchTrimControllerStage {
        return this.stage;
    }

    /**
     * 设置当前阶段。
     * @param stage 阶段。
     */
    public serviceSetStage(stage: SketchTrimControllerStage): void {
        this.stage = stage;
    }

    /**
     * 获取当前选项快照。
     * @returns 选项快照。
     */
    public serviceGetOptions(): SketchTrimOptions {
        return {...this.options};
    }

    /**
     * 清空强力剪裁撤销状态。
     */
    public serviceClearPowerUndoState(): void {
        this.clearPowerUndoState();
    }

    /**
     * 解析强力剪裁曲线对象。
     */
    public serviceResolvePowerTrimCurveById(objectId: string): SketchTrimCurveObject | null {
        return this.resolvePowerTrimCurveById(objectId);
    }

    /**
     * 解析强力剪裁目标曲线。
     */
    public serviceResolvePowerTrimTarget(entity: SketchTrimEntityRef): SketchTrimCurveObject | null {
        return this.resolvePowerTrimTarget(entity);
    }

    /**
     * 解析曲线数据。
     */
    public serviceResolveCurveData(curve: SketchTrimCurveObject, segmentIndex: number | null): SketchTrimCurveData {
        return this.geometryService.resolveCurveData(curve, segmentIndex);
    }

    /**
     * 解析曲线点击点。
     */
    public serviceResolveCurvePickPoint(curveData: SketchTrimCurveData, hitPoint?: [number, number, number]): THREE.Vector3 {
        return this.resolveCurvePickPoint(curveData, hitPoint);
    }

    /**
     * 投影点击点到曲线参数。
     */
    public serviceProjectPointToCurveParameter(curveData: SketchTrimCurveData, pickPoint: THREE.Vector3): number {
        return this.geometryService.projectPointToCurveParameter(curveData, pickPoint);
    }

    /**
     * 为强力剪裁模式收集单曲线剪裁边界参数。
     */
    public serviceCollectBoundaryParametersForPowerCurve(curveData: SketchTrimCurveData): number[] {
        return this.collectBoundaryParametersForCurve(curveData);
    }

    /**
     * 解析闭合曲线剪裁区间。
     */
    public serviceResolveClosedTrimIntervalByPick(boundaries: number[], pickT: number): {startT: number; endT: number} | null {
        return this.geometryService.resolveClosedTrimIntervalByPick(boundaries, pickT);
    }

    /**
     * 解析开放曲线剪裁区间。
     */
    public serviceResolveTrimIntervalByPick(boundaries: number[], pickT: number): {startT: number; endT: number} | null {
        return this.geometryService.resolveTrimIntervalByPick(boundaries, pickT);
    }

    /**
     * 应用曲线剪裁区间。
     */
    public serviceApplyCurveTrimInterval(curveData: SketchTrimCurveData, trimInterval: {startT: number; endT: number}): SketchTrimOperationResult {
        return this.applyCurveTrimInterval(curveData, trimInterval);
    }

    /**
     * 捕获曲线快照。
     */
    public serviceCaptureCurveSnapshot(curve: SketchTrimCurveObject): SketchTrimUpdatedCurveSnapshot | null {
        return this.captureCurveSnapshot(curve);
    }

    /**
     * 捕获被删除曲线快照。
     */
    public serviceCaptureRemovedCurveSnapshot(curve: SketchTrimCurveObject): SketchTrimRemovedLineSnapshot | null {
        return this.captureRemovedCurveSnapshot(curve);
    }

    /**
     * 恢复被删除曲线。
     */
    public serviceRestoreRemovedCurve(snapshot: SketchTrimRemovedLineSnapshot): void {
        this.restoreRemovedCurve(snapshot);
    }

    /**
     * 恢复曲线快照。
     */
    public serviceRestoreCurveSnapshot(snapshot: SketchTrimUpdatedCurveSnapshot): void {
        this.restoreCurveSnapshot(snapshot);
    }

    /**
     * 删除曲线实体。
     */
    public serviceRemoveCurveEntity(curve: SketchTrimCurveObject): string[] {
        return this.removeCurveEntity(curve);
    }

    /**
     * 删除曲线并返回可供智能尺寸消费的持久拓扑变化。
     *
     * @param curve 待删除曲线。
     * @returns 运行时删除 ID 与持久拓扑变化。
     */
    public serviceRemoveCurveEntityWithTopology(curve: SketchTrimCurveObject): SketchTrimCurveRemovalResult {
        return this.removeCurveEntityWithTopology(curve);
    }

    /**
     * 拾取曲线。
     */
    public servicePickTrimCurveAtClientPoint(clientX: number, clientY: number, ignoreConstructionGeometry: boolean): SketchTrimPickResult | null {
        return this.pickTrimCurveAtClientPoint(clientX, clientY, ignoreConstructionGeometry);
    }

    /**
     * 沿屏幕线段收集候选。
     */
    public servicePickSketchTrimEntitiesAlongClientSegment(
        mode: SketchTrimMode,
        fromX: number,
        fromY: number,
        toX: number,
        toY: number,
        ignoreConstructionGeometry: boolean,
    ): SketchTrimEntityRef[] {
        return this.pickSketchTrimEntitiesAlongClientSegment(mode, fromX, fromY, toX, toY, ignoreConstructionGeometry);
    }

    /**
     * 执行单实体操作。
     */
    public serviceApplySingleEntityOperation(
        mode: SketchTrimMode,
        target: SketchTrimEntityRef,
        silent: boolean,
    ): SketchTrimOperationResult {
        if (silent) {
            return this.applySingleEntityOperation(mode, target, silent);
        }

        return this.executeTrimHistoryTransaction(
            '裁剪草图实体',
            `sketch.runtime.trim.${mode}`,
            () => this.applySingleEntityOperation(mode, target, silent),
        );
    }

    /**
     * 将交点转换为实体引用。
     */
    public serviceCreateTrimEntityRefFromIntersection(picked: THREE.Intersection): SketchTrimEntityRef {
        return this.createTrimEntityRefFromIntersection(picked as THREE.Intersection<SketchTrimCurveObject>);
    }

    /**
     * 判断对象是否可作为 Corner 的开放曲线输入。
     *
     * @param object 待判断场景对象。
     * @returns 具备明确起终端点且支持自然路径计算时返回 true。
     */
    public serviceIsCornerSelectableCurve(object: unknown): object is SketchTrimCurveObject {
        return this.cornerGeometryService.isSupportedOpenCurveObject(object);
    }

    /**
     * 判断对象是否可作为 Inside/Outside 的边界实体。
     *
     * @param object 待判断场景对象。
     * @returns 属于当前 Trim 曲线矩阵且不是样式样条辅助线时返回 true。
     */
    public serviceIsInsideOutsideBoundaryObject(object: THREE.Object3D): object is SketchTrimCurveObject {
        return this.isInsideOutsideCurveObject(object);
    }

    /**
     * 判断对象是否可作为 TrimToClosest 的目标。
     *
     * 说明：
     * 1. 剪裁行为覆盖当前通用 Trim 曲线矩阵；
     * 2. 延伸行为保持现有直线准入，避免选择层承诺尚未实现的曲线延伸能力；
     * 3. 辅助对象、无限线、绘制预览和被忽略的构造几何不应显示为可操作目标。
     *
     * @param object 待判断场景对象。
     * @returns 当前行为允许选择时返回 true。
     */
    public serviceIsTrimToClosestSelectableEntity(object: THREE.Object3D): object is SketchTrimCurveObject {
        if (!this.serviceIsTrimCurveSelectableEntity(object)) {
            return false;
        }
        return this.options.singleEntityBehavior === SketchTrimSingleEntityBehavior.Trim
            || isSketchLineObject(object);
    }

    /**
     * 统一正式 Trim 曲线的候选资格，避免悬停、点击与延伸起点各维护一套白名单。
     * @param object 待判断对象。
     * @param ignoreConstructionGeometry 是否排除构造几何，默认读取当前命令选项。
     * @returns 可见且属于支持曲线、同时通过辅助及预览过滤时返回 true。
     */
    public serviceIsTrimCurveSelectableEntity(
        object: THREE.Object3D,
        ignoreConstructionGeometry = this.options.ignoreConstructionGeometry,
    ): object is SketchTrimCurveObject {
        return this.isInsideOutsideCurveObject(object)
            && !this.shouldExcludeTrimCandidate(object)
            && !(ignoreConstructionGeometry && object.isConstruction)
            && this.isObjectActuallyVisible(object);
    }

    /**
     * 判断对象是否可作为 Inside/Outside 的待裁剪目标。
     *
     * SolidWorks 的 Inside/Outside 只接受开放草图段作为目标，完整圆、完整椭圆和自闭合样条
     * 可以参与边界定义，但不能进入单闭合边界的第二项或双边界流程的第三项及后续目标位置。
     *
     * @param object 待判断场景对象。
     * @returns 支持且自身开放时返回 true。
     */
    public serviceIsInsideOutsideTargetObject(object: THREE.Object3D): object is SketchTrimCurveObject {
        return this.isInsideOutsideCurveObject(object)
            && !this.geometryService.isClosedCurveObject(object);
    }

    /**
     * 判断对象是否可单独构成 Inside/Outside 的闭合边界。
     *
     * @param object 待判断场景对象。
     * @returns 完整圆、完整椭圆、自闭合普通样条或自闭合样式样条返回 true。
     */
    public serviceIsInsideOutsideClosedBoundaryObject(object: THREE.Object3D): object is SketchTrimCurveObject {
        return this.isInsideOutsideCurveObject(object)
            && this.geometryService.isClosedCurveObject(object);
    }

    /**
     * 设置最近一次命中实体。
     */
    public serviceSetLastSelectedEntity(entity: SketchTrimEntityRef | null): void {
        this.lastSelectedEntity = entity;
    }

    /**
     * 克隆实体引用。
     */
    public serviceCloneTrimEntityRef(entity: SketchTrimEntityRef): SketchTrimEntityRef {
        return {
            ...entity,
            hitPoint: entity.hitPoint
                ? [...entity.hitPoint] as [number, number, number]
                : undefined,
        };
    }

    /**
     * 判断是否为同一实体。
     */
    public serviceIsSameTrimEntity(left: SketchTrimEntityRef, right: SketchTrimEntityRef): boolean {
        return this.isSameTrimEntity(left, right);
    }

    /**
     * 清空裁剪流程写入的选择。
     */
    public serviceClearTrimSelectionFromSelectionManager(): void {
        this.clearTrimSelectionFromSelectionManager();
    }

    /**
     * 失败后仅保留 Inside/Outside 已确定的边界选择。
     *
     * @param boundaries 需要保留的边界引用。
     */
    public serviceRetainInsideOutsideBoundarySelection(boundaries: readonly SketchTrimEntityRef[]): void {
        const boundaryObjects = boundaries
            .map((boundary) => this.resolvePowerTrimCurveById(boundary.objectId))
            .filter((curve): curve is SketchTrimCurveObject => curve !== null);
        this.syncTrimSelectionInSelectionManager(boundaryObjects);
    }

    /**
     * 发布结果消息。
     */
    public servicePublishResult(result: SketchTrimOperationResult): void {
        this.publishResult(result);
    }

    /**
     * 执行 TrimToClosest。
     */
    public serviceApplyTrimToClosestOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        return this.executeTrimHistoryTransaction(
            '裁剪草图实体',
            'sketch.runtime.trim.trim-to-closest',
            () => this.applyOperationWithDimensionTopology(() => this.applyTrimToClosestOperation(target)),
        );
    }

    /**
     * 执行 Corner。
     */
    public serviceApplyCornerOperation(firstEntity: SketchTrimEntityRef, secondEntity: SketchTrimEntityRef): SketchTrimOperationResult {
        return this.executeTrimHistoryTransaction(
            '裁剪草图拐角',
            'sketch.runtime.trim.corner',
            () => this.applyOperationWithDimensionTopology(
                () => this.applyCornerOperation(firstEntity, secondEntity),
            ),
        );
    }

    /**
     * 执行 Inside/Outside。
     */
    public serviceApplyInsideOutsideOperation(
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        boundaryA: SketchTrimEntityRef,
        boundaryB: SketchTrimEntityRef,
        targetEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {
        return this.executeTrimHistoryTransaction(
            '裁剪草图实体',
            `sketch.runtime.trim.${mode}`,
            () => this.applyOperationWithDimensionTopology(
                () => this.applyInsideOutsideOperation(mode, boundaryA, boundaryB, targetEntity),
            ),
        );
    }

    /**
     * 执行“单闭合边界 + 开放目标”的 Inside/Outside 操作。
     *
     * @param mode Inside 或 Outside 模式。
     * @param boundaryEntity 单个闭合边界。
     * @param targetEntity 开放目标实体。
     * @returns 操作结果。
     */
    public serviceApplyClosedBoundaryInsideOutsideOperation(
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        boundaryEntity: SketchTrimEntityRef,
        targetEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {
        return this.executeTrimHistoryTransaction(
            '裁剪草图实体',
            `sketch.runtime.trim.${mode}`,
            () => this.applyOperationWithDimensionTopology(
                () => this.applyClosedBoundaryInsideOutsideOperation(mode, boundaryEntity, targetEntity),
            ),
        );
    }

    /**
     * 在草图运行时历史事务中原子执行几何裁剪与尺寸仓库变化。
     *
     * @param label 历史标签。
     * @param commandType 历史命令类型。
     * @param work 已包含尺寸拓扑应用的裁剪工作。
     * @returns 裁剪结果。
     */
    private executeTrimHistoryTransaction(
        label: string,
        commandType: string,
        work: () => SketchTrimOperationResult,
    ): SketchTrimOperationResult {
        const dimensionStateBefore = this.dimensionIntegrationService.captureActiveState();
        let dimensionStateAfter: SketchTrimDimensionStateSnapshot | null = null;
        let dimensionPatches: EntityPatch[] = [];
        let dimensionStateRolledBack = false;
        let failedResult: SketchTrimOperationResult | null = null;
        const failedOperationRollbackSignal = {};

        try {
            return this.viewer.executeActiveSketchRuntimeHistoryTransaction({
                label,
                commandType,
                work: () => {
                    const result = work();

                    // Outside 需要连续裁掉目标曲线两侧。若第二侧失败，必须借助 Viewer 事务异常路径
                    // 恢复整组 before 快照，禁止仅返回 false 后把第一侧的半完成几何留在草图中。
                    if (!result.success) {
                        failedResult = result;
                        throw failedOperationRollbackSignal;
                    }

                    /**
                     * 几何与尺寸拓扑迁移完成后，只允许统一参数系统验证并写回最终状态。
                     * 拒绝候选时复用同一异常回滚通道恢复草图 before 与尺寸仓库。
                     */
                    if (!this.viewer.solveActiveSketchParametricTrimOperation()) {
                        failedResult = {
                            success: false,
                            message: '裁剪后的完整草图约束无法满足。',
                            createdEntityIds: [],
                            removedEntityIds: [],
                        };
                        throw failedOperationRollbackSignal;
                    }

                    // 尺寸状态必须在 work 内捕获，确保运行时快照与领域补丁表达同一操作终点。
                    dimensionStateAfter = this.dimensionIntegrationService.captureActiveState();
                    dimensionPatches = this.dimensionIntegrationService.createHistoryPatches(
                        dimensionStateBefore,
                        dimensionStateAfter,
                    );
                    return result;
                },
                shouldCommit: (result) => result.success,
                resolveExtraPatches: () => dimensionPatches,
                rollbackExtraState: () => {
                    this.dimensionIntegrationService.restoreStateIfCurrent(
                        dimensionStateAfter,
                        dimensionStateBefore,
                    );
                    dimensionStateRolledBack = true;
                },
            });
        } catch (error) {
            if (error === failedOperationRollbackSignal && failedResult) {
                if (!dimensionStateRolledBack) {
                    this.dimensionIntegrationService.restoreState(dimensionStateBefore);
                }
                return failedResult;
            }
            // Viewer 已先恢复草图运行时；此处再恢复仓库并重建尺寸视图，避免按失败终点计算标注布局。
            if (!dimensionStateRolledBack) {
                this.dimensionIntegrationService.restoreState(dimensionStateBefore);
            }
            throw error;
        }
    }

    /**
     * 执行几何裁剪并把其持久拓扑结果应用到智能尺寸。
     *
     * @param work 纯几何裁剪工作。
     * @returns 裁剪结果。
     */
    private applyOperationWithDimensionTopology(
        work: () => SketchTrimOperationResult,
    ): SketchTrimOperationResult {
        const result = work();

        if (result.success) {
            this.dimensionIntegrationService.applyTopologyChanges(result.topologyChanges ?? []);
        }

        return result;
    }

    /**
     * 执行单实体模式操作。
     * @param mode 模式。
     * @param target 目标实体。
     * @param silent 是否静默。
     */
    private applySingleEntityOperation(
        mode: SketchTrimMode,
        target: SketchTrimEntityRef,
        silent: boolean,
    ): SketchTrimOperationResult {
        let result: SketchTrimOperationResult;
        if (mode === SketchTrimMode.Power) {
            // Power 服务需要在几何与尺寸应用两侧捕获命令内撤销状态，因此由模式服务完成尺寸收口。
            result = this.getPowerModeService().executeSingleEntityOperation(target);
        } else if (mode === SketchTrimMode.TrimToClosest) {
            result = this.applyOperationWithDimensionTopology(
                () => this.applyTrimToClosestOperation(target),
            );
        } else {
            console.error(`[SketchTrimController] 当前模式不支持单实体执行：${mode}`);
            result = {
                success: false,
                message: '当前模式不支持单实体执行。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        if (!silent) {
            this.publishResult(result);
        }

        return result;
    }

    /**
     * 将选择命中转换为剪裁实体引用。
     * @param picked 命中结果。
     * @returns 剪裁实体引用。
     */
    private createTrimEntityRefFromIntersection(picked: THREE.Intersection<SketchTrimCurveObject>): SketchTrimEntityRef {
        return {
            objectId: picked.object.uuid,
            segmentIndex: null,
            hitPoint: [picked.point.x, picked.point.y, picked.point.z],
        };
    }

    /**
     * 清空裁剪工作流写入到选择管理器中的选中状态。
     * 说明：
     * 1. Corner 执行完成后要立即清掉两线选择，避免残留第二次执行；
     * 2. 退出裁剪实体时也必须走同一出口，防止界面仍保留“已选中线段”高亮；
     * 3. 通过抑制同步标记避免程序化清空再次回流触发 Corner 选择结算。
     */
    private clearTrimSelectionFromSelectionManager(): void {
        this.syncTrimSelectionInSelectionManager([]);
    }

    /**
     * 以程序方式同步裁剪流程写入到选择管理器中的选中集合。
     * @param entities 目标实体列表；为空时清空选中。
     */
    private syncTrimSelectionInSelectionManager(entities: SketchTrimCurveObject[]): void {
        const selectionManager = this.viewer.entitySelectionManager;
        if (!selectionManager) {
            return;
        }

        this.suppressTrimSelectionSync = true;
        try {
            if (entities.length <= 0) {
                selectionManager.clearSelectionProgrammatically();
                return;
            }
            selectionManager.selectEntitiesProgrammatically(entities);
        } finally {
            this.suppressTrimSelectionSync = false;
        }
    }

    /**
     * 执行 TrimToClosest 模式。
     * @param target 目标实体。
     * @returns 操作结果。
     */
    private applyTrimToClosestOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        if (this.options.singleEntityBehavior === SketchTrimSingleEntityBehavior.Extend) {
            return this.applyTrimToClosestExtendOperation(target);
        }
        return this.applyTrimToClosestTrimOperation(target);
    }

    /**
     * 执行“剪裁到最近端”语义。
     *
     * @param target 目标实体。
     * @returns 操作结果。
     */
    private applyTrimToClosestTrimOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        const curve = this.resolvePowerTrimTarget(target);
        if (!curve) {
            return {
                success: false,
                message: '未找到可操作曲线。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const curveData = this.geometryService.resolveCurveData(curve, target.segmentIndex ?? null);
        const pickPoint = this.resolveCurvePickPoint(curveData, target.hitPoint);
        const pickT = this.geometryService.projectPointToCurveParameter(curveData, pickPoint);
        const boundaries = this.collectBoundaryParametersForCurve(curveData);
        const trimInterval = curveData.isClosed
            ? this.geometryService.resolveClosedTrimIntervalByPick(boundaries, pickT)
            : this.geometryService.resolveTrimIntervalByPick(boundaries, pickT);
        if (!trimInterval) {
            return {
                success: false,
                message: '未找到可剪裁的最近端区段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        return this.applyCurveTrimInterval(curveData, trimInterval);
    }

    /**
     * 执行“延伸到最近边界”语义。
     *
     * @param target 目标实体。
     * @returns 操作结果。
     */
    private applyTrimToClosestExtendOperation(target: SketchTrimEntityRef): SketchTrimOperationResult {
        const line = this.resolveTargetLine(target);
        if (!line) {
            return {
                success: false,
                message: '未找到可操作线段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const lineData = this.geometryService.resolveLineData(line, target.segmentIndex ?? null);

        const pickPoint = this.resolvePickPoint(lineData, target.hitPoint);
        const pickT = this.geometryService.projectPointToSegmentParameter(lineData.start, lineData.end, pickPoint);
        const side = this.geometryService.resolveExtendSideByPick(pickT);

        const boundaries = this.collectBoundaryParametersForLine(lineData, true);
        const targetT = this.geometryService.resolveExtendTargetParameter(boundaries, side);
        if (targetT === null) {
            return {
                success: false,
                message: '未找到可延伸的目标边界。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const direction = lineData.end.clone().sub(lineData.start);
        const targetPoint = lineData.start.clone().addScaledVector(direction, targetT);

        if (side === SketchTrimExtendSide.Start) {
            line.startPoint.setPosition(targetPoint);
        } else {
            line.endPoint.setPosition(targetPoint);
        }

        line.updateGeometry();
        this.viewer.requestRender();

        return {
            success: true,
            message: '延伸成功。',
            createdEntityIds: [],
            removedEntityIds: [],
        };
    }

    /**
     * 执行 Corner 模式。
     * @param firstEntity 第一条线。
     * @param secondEntity 第二条线。
     * @returns 操作结果。
     */
    private applyCornerOperation(firstEntity: SketchTrimEntityRef, secondEntity: SketchTrimEntityRef): SketchTrimOperationResult {
        const firstCurve = this.resolveTargetCornerCurve(firstEntity);
        const secondCurve = this.resolveTargetCornerCurve(secondEntity);
        if (!firstCurve || !secondCurve || firstCurve === secondCurve) {
            return {
                success: false,
                message: '请选择两个不同的开放草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        if (isSketchLineObject(firstCurve) && isSketchLineObject(secondCurve)) {
            return this.applyLineCornerOperation(firstCurve, secondCurve, firstEntity, secondEntity);
        }
        return this.applyOpenCurveCornerOperation(firstCurve, secondCurve, firstEntity, secondEntity);
    }

    /**
     * 执行稳定的直线与直线 Corner 链路。
     *
     * @param firstLine 第一条直线。
     * @param secondLine 第二条直线。
     * @param firstEntity 第一条实体引用。
     * @param secondEntity 第二条实体引用。
     * @returns 操作结果。
     */
    private applyLineCornerOperation(
        firstLine: SketchLineObject,
        secondLine: SketchLineObject,
        firstEntity: SketchTrimEntityRef,
        secondEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {

        const firstLineData = this.geometryService.resolveLineData(firstLine, firstEntity.segmentIndex ?? null);
        const secondLineData = this.geometryService.resolveLineData(secondLine, secondEntity.segmentIndex ?? null);

        // Corner 可能替换所选线的端点对象，必须在任何拓扑写入前捕获两条线及其原端点身份。
        const firstSourceIdentity = this.captureCurveIdentitySnapshot(firstLine);
        const secondSourceIdentity = this.captureCurveIdentitySnapshot(secondLine);
        const firstSourceStartPoint = firstLine.startPoint;
        const firstSourceEndPoint = firstLine.endPoint;
        const secondSourceStartPoint = secondLine.startPoint;
        const secondSourceEndPoint = secondLine.endPoint;

        const intersection = this.geometryService.resolveLineIntersectionParameter(
            firstLineData.start,
            firstLineData.end,
            secondLineData.start,
            secondLineData.end,
            firstLineData.normal,
        );
        if (!intersection) {
            return {
                success: false,
                message: '两条线平行，无法创建角点。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        //------ Corner 点击侧保留行为 开始------
        const firstPickPoint = this.resolvePickPoint(firstLineData, firstEntity.hitPoint);
        const secondPickPoint = this.resolvePickPoint(secondLineData, secondEntity.hitPoint);
        const firstPickT = this.geometryService.projectPointToSegmentParameter(
            firstLineData.start,
            firstLineData.end,
            firstPickPoint,
        );
        const secondPickT = this.geometryService.projectPointToSegmentParameter(
            secondLineData.start,
            secondLineData.end,
            secondPickPoint,
        );

        const firstEndpointRole = this.geometryService.resolveEndpointRoleByIntersectionAndPick(
            intersection.targetT,
            firstPickT,
        );
        const secondEndpointRole = this.geometryService.resolveEndpointRoleByIntersectionAndPick(
            intersection.sourceT,
            secondPickT,
        );
        const firstSourceCornerPosition = firstEndpointRole === SketchTrimEndpointRole.Start
            ? firstLineData.start
            : firstLineData.end;
        const secondSourceCornerPosition = secondEndpointRole === SketchTrimEndpointRole.Start
            ? secondLineData.start
            : secondLineData.end;

        // 只有角点端发生真实位移时才让整线长度尺寸失效，已经位于虚拟角点的线保留原尺寸。
        const positionToleranceSquared = SketchTrimGeometryService.ENDPOINT_MERGE_POSITION_TOLERANCE ** 2;
        const firstLengthChanged = firstSourceCornerPosition.distanceToSquared(intersection.point)
            > positionToleranceSquared;
        const secondLengthChanged = secondSourceCornerPosition.distanceToSquared(intersection.point)
            > positionToleranceSquared;
        //------ Corner 点击侧保留行为 结束------

        this.cornerEndpointTopologyService.mergeToIntersection(
            firstLine,
            firstEndpointRole,
            secondLine,
            secondEndpointRole,
            intersection.point,
        );

        firstLine.updateGeometry();
        secondLine.updateGeometry();
        this.viewer.requestRender();

        return {
            success: true,
            message: '角点处理完成。',
            createdEntityIds: [],
            removedEntityIds: [],
            // 两条线继续保留实体身份；端点引用按真实改绑结果迁移，长度变化单独声明为不兼容。
            topologyChanges: [
                this.createCurveTopologyChange(
                    firstSourceIdentity,
                    firstLine,
                    [
                        this.createPointTopologyMapping(firstSourceStartPoint, firstLine.startPoint, firstLine),
                        this.createPointTopologyMapping(firstSourceEndPoint, firstLine.endPoint, firstLine),
                    ],
                    firstLengthChanged ? ['length'] : [],
                ),
                this.createCurveTopologyChange(
                    secondSourceIdentity,
                    secondLine,
                    [
                        this.createPointTopologyMapping(secondSourceStartPoint, secondLine.startPoint, secondLine),
                        this.createPointTopologyMapping(secondSourceEndPoint, secondLine.endPoint, secondLine),
                    ],
                    secondLengthChanged ? ['length'] : [],
                ),
            ],
        };
    }

    /**
     * 执行至少包含一条非直线开放曲线的 Corner。
     *
     * 该链路先完成纯几何决策；只有存在同时保留两侧点击段的自然交点时，
     * 才开始端点拓扑迁移和曲线定义写入，保证失败提示不会留下半完成状态。
     *
     * @param firstCurve 第一条开放曲线。
     * @param secondCurve 第二条开放曲线。
     * @param firstEntity 第一条实体引用。
     * @param secondEntity 第二条实体引用。
     * @returns 操作结果。
     */
    private applyOpenCurveCornerOperation(
        firstCurve: SketchTrimCurveObject,
        secondCurve: SketchTrimCurveObject,
        firstEntity: SketchTrimEntityRef,
        secondEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {
        const firstCurveData = this.geometryService.resolveCurveData(firstCurve, firstEntity.segmentIndex ?? null);
        const secondCurveData = this.geometryService.resolveCurveData(secondCurve, secondEntity.segmentIndex ?? null);
        const firstHitPoint = firstEntity.hitPoint
            ? new THREE.Vector3(...firstEntity.hitPoint)
            : this.geometryService.resolveCurvePointAtParameter(firstCurveData, 0.5);
        const secondHitPoint = secondEntity.hitPoint
            ? new THREE.Vector3(...secondEntity.hitPoint)
            : this.geometryService.resolveCurvePointAtParameter(secondCurveData, 0.5);
        const corner = this.cornerGeometryService.resolveCorner(
            firstCurveData,
            firstHitPoint,
            secondCurveData,
            secondHitPoint,
        );
        if (!corner) {
            return {
                success: false,
                message: '所选两个开放实体无法沿自然路径延伸并形成角点。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        // 任何拓扑写入前必须同时捕获实体定义、端点身份和尺寸持久引用映射源。
        const firstSnapshot = this.captureCurveSnapshot(firstCurve);
        const secondSnapshot = this.captureCurveSnapshot(secondCurve);
        if (!firstSnapshot || !secondSnapshot) {
            return {
                success: false,
                message: '无法捕获开放实体状态，角点操作未执行。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const firstIdentity = this.captureCurveIdentitySnapshot(firstCurve);
        const secondIdentity = this.captureCurveIdentitySnapshot(secondCurve);
        const firstSourceEndpoints = this.resolveCornerCurveEndpoints(firstCurve);
        const secondSourceEndpoints = this.resolveCornerCurveEndpoints(secondCurve);
        if (!firstSourceEndpoints || !secondSourceEndpoints) {
            return {
                success: false,
                message: '开放实体端点协议不完整，角点操作未执行。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const firstMovedPoint = corner.firstEndpointRole === SketchTrimEndpointRole.Start
            ? firstSourceEndpoints.startPoint
            : firstSourceEndpoints.endPoint;
        const secondMovedPoint = corner.secondEndpointRole === SketchTrimEndpointRole.Start
            ? secondSourceEndpoints.startPoint
            : secondSourceEndpoints.endPoint;
        const positionToleranceSquared = SketchTrimGeometryService.ENDPOINT_MERGE_POSITION_TOLERANCE ** 2;
        const firstGeometryChanged = firstMovedPoint.getPosition().distanceToSquared(corner.point)
            > positionToleranceSquared;
        const secondGeometryChanged = secondMovedPoint.getPosition().distanceToSquared(corner.point)
            > positionToleranceSquared;

        const collectionStates = new Map<SketchCollectionObject, boolean>();
        [firstCurve.parent, secondCurve.parent].forEach((parent) => {
            if (isSketchCollectionObject(parent) && !collectionStates.has(parent)) {
                collectionStates.set(parent, parent.isClosedGeometry);
            }
        });

        // 先保护外部共享点并完成端点身份改绑，再更新曲线定义，禁止定义同步移动未选第三条实体。
        const mergedCornerPoint = this.cornerEndpointTopologyService.mergeToIntersection(
            firstCurve,
            corner.firstEndpointRole,
            secondCurve,
            corner.secondEndpointRole,
            corner.point,
        );
        if (!mergedCornerPoint) {
            return {
                success: false,
                message: '开放实体端点拓扑合并失败，角点操作已回滚。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const firstCurveUpdated = this.updateCornerCurveFromSnapshot(
            firstSnapshot,
            corner.firstEndpointRole,
            corner.firstParameterT,
        );
        const secondCurveUpdated = this.updateCornerCurveFromSnapshot(
            secondSnapshot,
            corner.secondEndpointRole,
            corner.secondParameterT,
        );
        if (!firstCurveUpdated || !secondCurveUpdated) {
            return {
                success: false,
                message: '开放实体曲线更新失败，角点操作已回滚。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        collectionStates.forEach((wasClosedGeometry, collection) => {
            this.syncCollectionContourAfterTopologyMutation(collection, wasClosedGeometry);
        });
        this.viewer.requestRender();

        const firstTargetEndpoints = this.resolveCornerCurveEndpoints(firstCurve);
        const secondTargetEndpoints = this.resolveCornerCurveEndpoints(secondCurve);
        if (!firstTargetEndpoints || !secondTargetEndpoints) {
            return {
                success: false,
                message: '角点操作后无法解析开放实体端点，操作已回滚。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        return {
            success: true,
            message: '开放实体角点处理完成。',
            createdEntityIds: [],
            removedEntityIds: [],
            topologyChanges: [
                this.createCurveTopologyChange(
                    firstIdentity,
                    firstCurve,
                    [
                        this.createPointTopologyMapping(
                            firstSourceEndpoints.startPoint,
                            firstTargetEndpoints.startPoint,
                            firstCurve,
                        ),
                        this.createPointTopologyMapping(
                            firstSourceEndpoints.endPoint,
                            firstTargetEndpoints.endPoint,
                            firstCurve,
                        ),
                    ],
                    firstGeometryChanged ? this.resolveCornerInvalidatedDimensionKinds(firstSnapshot) : [],
                ),
                this.createCurveTopologyChange(
                    secondIdentity,
                    secondCurve,
                    [
                        this.createPointTopologyMapping(
                            secondSourceEndpoints.startPoint,
                            secondTargetEndpoints.startPoint,
                            secondCurve,
                        ),
                        this.createPointTopologyMapping(
                            secondSourceEndpoints.endPoint,
                            secondTargetEndpoints.endPoint,
                            secondCurve,
                        ),
                    ],
                    secondGeometryChanged ? this.resolveCornerInvalidatedDimensionKinds(secondSnapshot) : [],
                ),
            ],
        };
    }

    /**
     * 解析开放曲线的起终拓扑点。
     *
     * @param curve 开放曲线。
     * @returns 起终点对象；目标不是开放曲线时返回 null。
     */
    private resolveCornerCurveEndpoints(curve: SketchTrimCurveObject): {
        startPoint: SketchPointObject;
        endPoint: SketchPointObject;
    } | null {
        if (isSketchLineObject(curve)) {
            return {startPoint: curve.startPoint, endPoint: curve.endPoint};
        }
        if (isSketchArcObject(curve) || isSketchEllipseObject(curve) || isSketchParabolaObject(curve)) {
            return {startPoint: curve.getStartPoint(), endPoint: curve.getEndPoint()};
        }
        if (isSketchSplineObject(curve) || isSketchStyleSplineObject(curve)) {
            return {startPoint: curve.startPoint, endPoint: curve.endPoint};
        }
        console.error(`[SketchTrimController] Corner 端点解析失败：实体 ${curve.uuid} 不是开放曲线。`);
        return null;
    }

    /**
     * 根据操作前快照把曲线一侧更新到 Corner 参数。
     *
     * @param snapshot 曲线操作前快照。
     * @param endpointRole 被移动的端点角色。
     * @param parameterT 角点在原支撑曲线上的参数。
     * @returns 更新成功返回 true；快照类型不受支持时返回 false。
     */
    private updateCornerCurveFromSnapshot(
        snapshot: SketchTrimUpdatedCurveSnapshot,
        endpointRole: SketchTrimEndpointRole,
        parameterT: number,
    ): boolean {
        const range = endpointRole === SketchTrimEndpointRole.Start
            ? {startT: parameterT, endT: 1}
            : {startT: 0, endT: parameterT};
        if (snapshot.curveKind === SketchTrimCurveKind.Line) {
            snapshot.line.updateGeometry();
            this.syncMaterialSnapshot(snapshot.line.startPoint);
            this.syncMaterialSnapshot(snapshot.line.endPoint);
            this.syncMaterialSnapshot(snapshot.line);
            return true;
        }
        if (snapshot.curveKind === SketchTrimCurveKind.Arc) {
            this.updateArcToRange(snapshot.line, snapshot.definition, range);
            return true;
        }
        if (snapshot.curveKind === SketchTrimCurveKind.Ellipse) {
            this.updateEllipseToRange(snapshot.line, snapshot.definition, range);
            return true;
        }
        if (snapshot.curveKind === SketchTrimCurveKind.Parabola) {
            const startParameter = THREE.MathUtils.lerp(
                snapshot.definition.startParameter,
                snapshot.definition.endParameter,
                range.startT,
            );
            const endParameter = THREE.MathUtils.lerp(
                snapshot.definition.startParameter,
                snapshot.definition.endParameter,
                range.endT,
            );
            snapshot.line.updateParabolaDefinition({
                ...snapshot.definition,
                startParameter,
                endParameter,
            });
            this.syncMaterialSnapshot(snapshot.line.getStartPoint());
            this.syncMaterialSnapshot(snapshot.line.getEndPoint());
            this.syncMaterialSnapshot(snapshot.line.getFocalPoint());
            this.syncMaterialSnapshot(snapshot.line.getApexPoint());
            this.syncMaterialSnapshot(snapshot.line);
            return true;
        }
        if (
            snapshot.curveKind === SketchTrimCurveKind.Spline
            || snapshot.curveKind === SketchTrimCurveKind.StyleSpline
        ) {
            const curveData = this.geometryService.resolveCurveData(snapshot.line);
            this.updateSplineToEdgeRange(
                curveData,
                snapshot.controlPointPositions.map((position) => new THREE.Vector3(...position)),
                snapshot.curveMetadata,
                range,
            );
            return true;
        }
        console.error(`[SketchTrimController] Corner 曲线更新失败：不支持闭合曲线类型 ${snapshot.curveKind}。`);
        return false;
    }

    /**
     * 解析 Corner 改变曲线跨度后不再兼容的整实体尺寸类型。
     *
     * @param snapshot 曲线操作前快照。
     * @returns 需要删除的尺寸类型。
     */
    private resolveCornerInvalidatedDimensionKinds(
        snapshot: SketchTrimUpdatedCurveSnapshot,
    ): SketchDimensionKind[] {
        if (snapshot.curveKind === SketchTrimCurveKind.Line) {
            return ['length'];
        }
        if (snapshot.curveKind === SketchTrimCurveKind.Arc) {
            return ['arc-length'];
        }
        if (
            snapshot.curveKind === SketchTrimCurveKind.Spline
            || snapshot.curveKind === SketchTrimCurveKind.StyleSpline
        ) {
            return ['spline-length'];
        }
        return [];
    }

    /**
     * 执行 Inside / Outside 模式。
     * @param mode 模式。
     * @param boundaryA 第一边界。
     * @param boundaryB 第二边界。
     * @param targetEntity 目标实体。
     * @returns 操作结果。
     */
    private applyInsideOutsideOperation(
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        boundaryA: SketchTrimEntityRef,
        boundaryB: SketchTrimEntityRef,
        targetEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {
        const boundaryACurve = this.resolvePowerTrimTarget(boundaryA);
        const boundaryBCurve = this.resolvePowerTrimTarget(boundaryB);
        const targetCurve = this.resolvePowerTrimTarget(targetEntity);
        if (!boundaryACurve || !boundaryBCurve || !targetCurve) {
            return {
                success: false,
                message: '未找到可操作草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        if (
            boundaryACurve.uuid === boundaryBCurve.uuid
            || boundaryACurve.uuid === targetCurve.uuid
            || boundaryBCurve.uuid === targetCurve.uuid
        ) {
            return {
                success: false,
                message: '两条边界与待裁剪目标必须是三个不同的草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const boundaryAData = this.geometryService.resolveCurveData(boundaryACurve, boundaryA.segmentIndex ?? null);
        const boundaryBData = this.geometryService.resolveCurveData(boundaryBCurve, boundaryB.segmentIndex ?? null);
        const targetData = this.geometryService.resolveCurveData(targetCurve, targetEntity.segmentIndex ?? null);
        if (targetData.isClosed) {
            return {
                success: false,
                message: '在内剪除和在外剪除只支持开放草图实体作为待裁剪目标。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        // 两条边界必须分别求交，不能把交点混成一个列表后丢失边界归属。
        const boundaryAParameters = this.collectInsideOutsideBoundaryParameters(targetData, boundaryAData);
        const boundaryBParameters = this.collectInsideOutsideBoundaryParameters(targetData, boundaryBData);

        if (boundaryAParameters.length === 0 && boundaryBParameters.length === 0) {
            return this.applyInsideOutsideWholeTargetWithoutIntersections(
                mode,
                boundaryAData,
                boundaryBData,
                targetData,
            );
        }

        if (boundaryAParameters.length !== 1 || boundaryBParameters.length !== 1) {
            return {
                success: false,
                message: '目标实体必须与两条边界各横穿一次，或与两条边界都不相交。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const boundaryAParameter = boundaryAParameters[0]!;
        const boundaryBParameter = boundaryBParameters[0]!;
        if (
            !this.isInsideOutsideIntersectionTransverse(targetData, boundaryAData, boundaryAParameter)
            || !this.isInsideOutsideIntersectionTransverse(targetData, boundaryBData, boundaryBParameter)
        ) {
            return {
                success: false,
                message: '目标实体与边界相切，未形成可判定的内外穿越区间。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const interval = {
            startT: Math.min(boundaryAParameter, boundaryBParameter),
            endT: Math.max(boundaryAParameter, boundaryBParameter),
        };
        if (interval.endT - interval.startT <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return {
                success: false,
                message: '两条边界在目标实体上没有形成有效间距。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        return mode === SketchTrimMode.TrimAwayInside
            ? this.applyCurveTrimInterval(targetData, interval)
            : this.applyOutsideKeepMiddleInterval(targetData, interval);
    }

    /**
     * 执行单个闭合边界的 Inside/Outside 模式。
     *
     * 第一项完整圆、完整椭圆或自闭合样条已经独立定义一个有限内外区域，因此第二项开放目标
     * 与该边界形成两个横穿交点时即可直接裁剪，不再等待第二条边界。
     *
     * @param mode Inside 或 Outside 模式。
     * @param boundaryEntity 单个闭合边界。
     * @param targetEntity 开放目标实体。
     * @returns 操作结果。
     */
    private applyClosedBoundaryInsideOutsideOperation(
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        boundaryEntity: SketchTrimEntityRef,
        targetEntity: SketchTrimEntityRef,
    ): SketchTrimOperationResult {
        const boundaryCurve = this.resolvePowerTrimTarget(boundaryEntity);
        const targetCurve = this.resolvePowerTrimTarget(targetEntity);
        if (!boundaryCurve || !targetCurve) {
            return {
                success: false,
                message: '未找到可操作草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        if (boundaryCurve.uuid === targetCurve.uuid) {
            return {
                success: false,
                message: '闭合边界与待裁剪目标必须是两个不同的草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const boundaryData = this.geometryService.resolveCurveData(
            boundaryCurve,
            boundaryEntity.segmentIndex ?? null,
        );
        const targetData = this.geometryService.resolveCurveData(
            targetCurve,
            targetEntity.segmentIndex ?? null,
        );
        if (!boundaryData.isClosed) {
            return {
                success: false,
                message: '第一项不是可独立定义区域的闭合草图实体。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        if (targetData.isClosed) {
            return {
                success: false,
                message: '在内剪除和在外剪除只支持开放草图实体作为待裁剪目标。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const boundaryParameters = this.geometryService.collectCurveBoundaryParameters(
            targetData,
            [boundaryData],
            false,
        );
        if (boundaryParameters.length !== 2) {
            return {
                success: false,
                message: '开放目标必须与闭合边界横穿并形成两个不同交点。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const startT = Math.min(boundaryParameters[0]!, boundaryParameters[1]!);
        const endT = Math.max(boundaryParameters[0]!, boundaryParameters[1]!);
        if (endT - startT <= SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            return {
                success: false,
                message: '开放目标与闭合边界的两个交点发生重合。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        if (
            !this.isInsideOutsideIntersectionTransverse(targetData, boundaryData, startT)
            || !this.isInsideOutsideIntersectionTransverse(targetData, boundaryData, endT)
        ) {
            return {
                success: false,
                message: '开放目标与闭合边界相切，未形成可判定的内外区间。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const interval = {startT, endT};
        return mode === SketchTrimMode.TrimAwayInside
            ? this.applyCurveTrimInterval(targetData, interval)
            : this.applyOutsideKeepMiddleInterval(targetData, interval);
    }

    /**
     * 收集单条 Inside/Outside 边界在目标曲线上的参数。
     *
     * 直线边界按无限支撑线求交，确保短边界可见段外的交点仍参与区域裁剪；其它曲线边界
     * 继续复用通用曲线求交矩阵，并严格保留边界归属。
     *
     * @param targetData 开放目标曲线。
     * @param boundaryData 单条边界曲线。
     * @returns 目标曲线参数列表。
     */
    private collectInsideOutsideBoundaryParameters(
        targetData: SketchTrimCurveData,
        boundaryData: SketchTrimCurveData,
    ): number[] {
        if (boundaryData.curveKind === SketchTrimCurveKind.Line) {
            const boundaryLineData = this.geometryService.resolveLineData(
                boundaryData.curve as SketchLineObject,
                boundaryData.segmentIndex,
            );
            return this.geometryService.collectCurveBoundaryParametersAgainstLineSupport(
                targetData,
                boundaryLineData,
            );
        }
        return this.geometryService.collectCurveBoundaryParameters(targetData, [boundaryData], false);
    }

    /**
     * 处理目标与两条边界都不相交的 Inside/Outside 场景。
     *
     * 当前只有两条直线的无限支撑边界能够唯一划分“之间/之外”。任意曲线边界若没有交点，
     * 在缺少闭合区域或点击侧语义时可能对应多个区域，因此必须失败且不改变模型。
     *
     * @param mode Inside 或 Outside 模式。
     * @param boundaryAData 第一边界曲线数据。
     * @param boundaryBData 第二边界曲线数据。
     * @param targetData 开放目标曲线数据。
     * @returns 整体删除结果或不可判定结果。
     */
    private applyInsideOutsideWholeTargetWithoutIntersections(
        mode: SketchTrimMode.TrimAwayInside | SketchTrimMode.TrimAwayOutside,
        boundaryAData: SketchTrimCurveData,
        boundaryBData: SketchTrimCurveData,
        targetData: SketchTrimCurveData,
    ): SketchTrimOperationResult {
        if (
            boundaryAData.curveKind !== SketchTrimCurveKind.Line
            || boundaryBData.curveKind !== SketchTrimCurveKind.Line
        ) {
            return {
                success: false,
                message: '当前曲线边界未形成可唯一判定的内外区域。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const boundaryALineData = this.geometryService.resolveLineData(
            boundaryAData.curve as SketchLineObject,
            boundaryAData.segmentIndex,
        );
        const boundaryBLineData = this.geometryService.resolveLineData(
            boundaryBData.curve as SketchLineObject,
            boundaryBData.segmentIndex,
        );
        const probePoint = this.geometryService.resolveCurvePointAtParameter(targetData, 0.5);
        const isInsideBoundaryRegion = this.geometryService.isPointBetweenBoundaries(
            boundaryALineData.start,
            boundaryALineData.end,
            boundaryBLineData.start,
            boundaryBLineData.end,
            probePoint,
            targetData.normal,
        );
        const shouldTrimWholeTarget = mode === SketchTrimMode.TrimAwayInside
            ? isInsideBoundaryRegion
            : !isInsideBoundaryRegion;
        if (!shouldTrimWholeTarget) {
            return {
                success: false,
                message: '目标实体位于当前模式的保留区域，无需裁剪。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        return this.applyCurveTrimInterval(targetData, {startT: 0, endT: 1});
    }

    /**
     * 判断目标曲线与边界曲线的唯一交点是否为横穿交点。
     *
     * @param targetData 待裁剪目标曲线。
     * @param boundaryData 边界曲线。
     * @param targetParameter 交点在目标曲线上的参数。
     * @returns 两条曲线切向不平行时返回 true。
     */
    private isInsideOutsideIntersectionTransverse(
        targetData: SketchTrimCurveData,
        boundaryData: SketchTrimCurveData,
        targetParameter: number,
    ): boolean {
        const intersectionPoint = this.geometryService.resolveCurvePointAtParameter(targetData, targetParameter);
        const boundaryParameter = this.geometryService.projectPointToCurveParameter(boundaryData, intersectionPoint);
        const targetTangent = this.resolveInsideOutsideCurveTangent(targetData, targetParameter);
        const boundaryTangent = this.resolveInsideOutsideCurveTangent(boundaryData, boundaryParameter);
        if (!targetTangent || !boundaryTangent) {
            return false;
        }

        const normalizedTargetTangent = targetTangent.normalize();
        const normalizedBoundaryTangent = boundaryTangent.normalize();
        const sine = Math.abs(
            normalizedTargetTangent
                .cross(normalizedBoundaryTangent)
                .dot(targetData.normal),
        );
        return sine > SketchTrimGeometryService.ANGLE_TOLERANCE;
    }

    /**
     * 使用曲线参数邻域解析稳定切向。
     *
     * @param curveData 曲线数据。
     * @param parameterT 目标参数。
     * @returns 非退化切向；邻域重合时返回 null。
     */
    private resolveInsideOutsideCurveTangent(
        curveData: SketchTrimCurveData,
        parameterT: number,
    ): THREE.Vector3 | null {
        const parameterStep = 1e-4;
        const startT = THREE.MathUtils.clamp(parameterT - parameterStep, 0, 1);
        const endT = THREE.MathUtils.clamp(parameterT + parameterStep, 0, 1);
        const tangent = this.geometryService.resolveCurvePointAtParameter(curveData, endT)
            .sub(this.geometryService.resolveCurvePointAtParameter(curveData, startT));
        if (tangent.lengthSq() <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE_SQUARED) {
            return null;
        }
        return tangent;
    }

    /**
     * 执行 Outside 的“保留两交点之间中段”操作。
     *
     * 现有曲线裁剪原语表达单个连续删除区间，因此这里按参数从右到左连续删除两侧，
     * 避免第一次更新曲线后破坏左侧原始参数换算。失败由外层历史事务恢复整组 before 快照。
     *
     * @param targetData 开放目标曲线数据。
     * @param keptInterval 需要保留的原始曲线参数区间。
     * @returns 两侧删除的聚合结果。
     */
    private applyOutsideKeepMiddleInterval(
        targetData: SketchTrimCurveData,
        keptInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const sourceSnapshot = this.captureCurveSnapshot(targetData.curve);
        if (!sourceSnapshot) {
            return {
                success: false,
                message: '无法捕获目标曲线状态，在外剪除未执行。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const sourceIdentity = this.captureCurveIdentitySnapshot(targetData.curve);
        const sourceEndpoints = this.resolveCornerCurveEndpoints(targetData.curve);
        if (!sourceEndpoints) {
            return {
                success: false,
                message: '目标曲线端点协议不完整，在外剪除未执行。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }
        const operationResults: SketchTrimOperationResult[] = [];
        let retainedOriginalEnd = 1;

        // 先裁右侧，左侧交点在剩余曲线上的局部参数即可按原始保留终点换算。
        if (keptInterval.endT < 1 - SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            const rightResult = this.applyCurveTrimInterval(targetData, {
                startT: keptInterval.endT,
                endT: 1,
            });
            operationResults.push(rightResult);
            if (!rightResult.success) {
                return rightResult;
            }
            retainedOriginalEnd = keptInterval.endT;
        }

        if (keptInterval.startT > SketchTrimGeometryService.TOPOLOGY_TOLERANCE) {
            const currentCurve = this.resolvePowerTrimCurveById(targetData.curve.uuid);
            if (!currentCurve) {
                return {
                    success: false,
                    message: '裁剪右侧后无法解析目标曲线，在外剪除已回滚。',
                    createdEntityIds: [],
                    removedEntityIds: [],
                };
            }
            const currentCurveData = this.geometryService.resolveCurveData(currentCurve, targetData.segmentIndex);
            const localStartT = keptInterval.startT / retainedOriginalEnd;
            const leftResult = this.applyCurveTrimInterval(currentCurveData, {
                startT: 0,
                endT: localStartT,
            });
            operationResults.push(leftResult);
            if (!leftResult.success) {
                return leftResult;
            }
        }

        if (operationResults.length === 0) {
            return {
                success: false,
                message: '目标实体没有位于两条边界之外的可剪裁区段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const createdEntityIds = operationResults.flatMap((result) => result.createdEntityIds);
        const removedEntityIds = operationResults.flatMap((result) => result.removedEntityIds);
        const topologyChanges = operationResults.flatMap((result) => result.topologyChanges ?? []);
        const finalCurve = this.resolvePowerTrimCurveById(targetData.curve.uuid);
        if (!finalCurve) {
            return {
                success: false,
                message: '无法解析在外剪除后的目标曲线，操作已回滚。',
                createdEntityIds,
                removedEntityIds,
            };
        }
        const finalEndpoints = this.resolveCornerCurveEndpoints(finalCurve);
        if (!finalEndpoints) {
            return {
                success: false,
                message: '无法解析在外剪除后的目标曲线端点，操作已回滚。',
                createdEntityIds,
                removedEntityIds,
            };
        }
        const finalTargetTopology = this.createCurveTopologyChange(
            sourceIdentity,
            finalCurve,
            [
                this.createPointTopologyMapping(sourceEndpoints.startPoint, finalEndpoints.startPoint, finalCurve),
                this.createPointTopologyMapping(sourceEndpoints.endPoint, finalEndpoints.endPoint, finalCurve),
            ],
            this.resolveCornerInvalidatedDimensionKinds(sourceSnapshot),
        );

        return {
            success: true,
            message: '在外剪除完成。',
            createdEntityIds,
            removedEntityIds,
            // 同一实体的一次或两次裁剪都只向尺寸层提交最终映射；保留槽口等其它源实体的拓扑变化。
            topologyChanges: [
                ...topologyChanges.filter((change) => change.sourceEntityId !== sourceIdentity.entityId),
                finalTargetTopology,
            ],
        };
    }

    /**
     * 应用剪裁区间。
     * @param lineData 线段数据。
     * @param trimInterval 剪裁区间。
     * @returns 操作结果。
     */
    private applyTrimInterval(lineData: SketchTrimLineData, trimInterval: {startT: number; endT: number}): SketchTrimOperationResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(lineData.line);
        const sourceStartPoint = lineData.line.startPoint;
        const sourceEndPoint = lineData.line.endPoint;
        const segments = this.geometryService.buildLineSegmentsAfterTrim(
            lineData.start,
            lineData.end,
            trimInterval,
        );

        if (!segments.trimmedSegment) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];
        const affectedClosureSeeds: CollectSource[] = [];

        //------ 强力裁剪约束清理 开始------
        // SOLIDWORKS 强力裁剪会直接改变草图实体拓扑；执行删除、缩短或拆分前必须先解除被裁剪曲线及其专属端点上的几何关系。
        // 若整条线仅按“保留为构造线”转换属性，则对象和端点仍然有效，不应主动清理现有关系。
        if (!(segments.keptSegments.length === 0 && this.options.keepTrimmedAsConstruction)) {
            this.constraintCleanupService.cleanupCurveConstraints(
                lineData.line,
                this.viewer.sketchGroupManager?.activeSketchGroup ?? null,
            );
        }
        //------ 强力裁剪约束清理 结束------

        //------ 强力剪裁槽口开链降级 开始------
        // 槽口内部边被裁剪前必须先脱离闭合槽口容器，否则旧轮廓会在开链后继续显示。
        const slotDissolveResult = this.slotTopologyService.dissolveSlotForTrimTarget(lineData.line);
        removedEntityIds.push(...slotDissolveResult.removedEntityIds);
        topologyChanges.push(...slotDissolveResult.topologyChanges);
        //------ 强力剪裁槽口开链降级 结束------

        if (segments.keptSegments.length === 0) {
            if (this.options.keepTrimmedAsConstruction) {
                lineData.line.isConstruction = true;
                lineData.line.updateGeometry();
                this.viewer.requestRender();
                return {
                    success: true,
                    message: '整段已保留为构造线。',
                    createdEntityIds,
                    removedEntityIds,
                    topologyChanges,
                };
            }

            //------ 草图闭合模块化 开始------
            // 整段删除会把原闭合集合降级为开链；此时必须用剩余边重新触发统一闭合解析，
            // 让“矩形三边 + 圆裁剪保留弧”这类 SOLIDWORKS 轮廓在第二次裁剪后能收口。
            const closureSeedsAfterRemoval = this.resolveClosureSeedsAfterWholeCurveRemoval(lineData.line);
            //------ 草图闭合模块化 结束------
            const removalResult = this.removeCurveEntityWithTopology(lineData.line);
            removedEntityIds.push(...removalResult.removedEntityIds);
            topologyChanges.push(...removalResult.topologyChanges);
            //------ 草图闭合模块化 开始------
            createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim(closureSeedsAfterRemoval));
            //------ 草图闭合模块化 结束------
            this.viewer.requestRender();
            return {
                success: true,
                message: '线段已删除。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        if (segments.keptSegments.length === 1) {
            const lineParent = lineData.line.parent;
            const collectionParent = isSketchCollectionObject(lineParent)
                ? lineParent
                : null;
            const wasClosedGeometry = collectionParent?.isClosedGeometry ?? false;

            this.updateLineToSegment(lineData.line, segments.keptSegments[0]);

            // 单段保留仍由原线继承实体身份，但被剪侧端点可能已替换为新的持久点。
            topologyChanges.push(this.createCurveTopologyChange(
                sourceIdentity,
                lineData.line,
                [
                    this.createPointTopologyMapping(sourceStartPoint, lineData.line.startPoint, lineData.line),
                    this.createPointTopologyMapping(sourceEndPoint, lineData.line.endPoint, lineData.line),
                ],
                ['length'],
            ));

            if (collectionParent) {
                this.syncCollectionContourAfterTopologyMutation(collectionParent, wasClosedGeometry);
            }
            affectedClosureSeeds.push(lineData.line);
        } else if (segments.keptSegments.length === 2) {
            const splitLines = this.splitLineByKeptSegments(lineData.line, segments.keptSegments);

            // 中段裁剪会把一条直线变成两条独立实体，原单实体尺寸已没有唯一、兼容的继承目标。
            // 必须由统一尺寸拓扑服务删除所有引用原直线的尺寸，禁止把尺寸猜测迁移到任一保留段。
            topologyChanges.push(this.createCurveTopologyChange(sourceIdentity, null));
            affectedClosureSeeds.push(...splitLines);
            createdEntityIds.push(...splitLines.slice(1).map((line) => line.uuid));
        } else {
            console.error(`[SketchTrimController] 剪裁结果异常：保留段数量非法，线段=${lineData.line.uuid}，保留段数量=${segments.keptSegments.length}。`);
            return {
                success: false,
                message: '剪裁结果异常：保留段数量非法。',
                createdEntityIds,
                removedEntityIds,
            };
        }

        createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim(affectedClosureSeeds));

        if (this.options.keepTrimmedAsConstruction) {
            createdEntityIds.push(this.createConstructionBackupSegment(lineData.line, segments.trimmedSegment));
        }

        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    /**
     * 应用曲线剪裁区间。
     * @param curveData 曲线数据。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applyCurveTrimInterval(curveData: SketchTrimCurveData, trimInterval: {startT: number; endT: number}): SketchTrimOperationResult {
        if (curveData.curveKind === SketchTrimCurveKind.Line) {
            return this.applyTrimInterval(
                this.geometryService.resolveLineData(curveData.curve as SketchLineObject, curveData.segmentIndex),
                trimInterval,
            );
        }

        if (curveData.curveKind === SketchTrimCurveKind.Arc) {
            return this.applyArcTrimInterval(curveData.curve as SketchArcObject, trimInterval);
        }

        if (curveData.curveKind === SketchTrimCurveKind.Circle) {
            return this.applyCircleTrimInterval(curveData.curve as SketchCircleObject, trimInterval);
        }

        //------ 样条精确裁剪 开始------
        if (curveData.curveKind === SketchTrimCurveKind.Parabola) {
            return this.applyParabolaTrimInterval(curveData.curve as SketchParabolaObject, trimInterval);
        }

        if (
            curveData.curveKind === SketchTrimCurveKind.Spline
            || curveData.curveKind === SketchTrimCurveKind.StyleSpline
        ) {
            return this.applySplineTrimInterval(curveData, trimInterval);
        }
        //------ 样条精确裁剪 结束------

        return this.applyEllipseTrimInterval(curveData.curve as SketchEllipseObject, curveData.isClosed, trimInterval);
    }

    /**
     * 应用圆弧剪裁区间。
     * @param arc 目标圆弧。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applyArcTrimInterval(
        arc: SketchArcObject,
        trimInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(arc);
        const sourceStartPoint = arc.getStartPoint();
        const sourceEndPoint = arc.getEndPoint();
        const sourceDefinition = arc.getArcDefinition();
        const {keptRanges, trimmedRange} = this.geometryService.buildOpenCurveRangesAfterTrim(trimInterval);
        if (!trimmedRange) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];
        const affectedClosureSeeds: CollectSource[] = [];

        //------ 强力剪裁槽口开链降级 开始------
        // 圆弧槽口端帽或圆弧槽口内部弧被裁剪时，同样需要解除槽口闭合语义。
        const slotDissolveResult = this.slotTopologyService.dissolveSlotForTrimTarget(arc);
        removedEntityIds.push(...slotDissolveResult.removedEntityIds);
        topologyChanges.push(...slotDissolveResult.topologyChanges);
        //------ 强力剪裁槽口开链降级 结束------

        if (keptRanges.length === 0) {
            if (this.options.keepTrimmedAsConstruction) {
                arc.isConstruction = true;
                arc.updateArc();
                this.viewer.requestRender();
                return {
                    success: true,
                    message: '整段已保留为构造线。',
                    createdEntityIds,
                    removedEntityIds,
                    topologyChanges,
                };
            }

            const removalResult = this.removeCurveEntityWithTopology(arc);
            removedEntityIds.push(...removalResult.removedEntityIds);
            topologyChanges.push(...removalResult.topologyChanges);
            this.viewer.requestRender();
            return {
                success: true,
                message: '曲线已删除。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        const container = arc.parent;
        const secondArc = keptRanges.length === 2
            ? this.createArcSegmentFromDefinition(arc, sourceDefinition, keptRanges[1], arc.isConstruction)
            : null;
        this.updateArcToRange(arc, sourceDefinition, keptRanges[0]);

        if (secondArc) {
            createdEntityIds.push(secondArc.uuid);
            this.attachSplitCurveToContainer(container, arc, secondArc);
            affectedClosureSeeds.push(secondArc);
        }

        // 中间剪除时原终点迁移到第二段；单侧剪除时两个端点语义均由规范保留段承接。
        topologyChanges.push(this.createCurveTopologyChange(
            sourceIdentity,
            arc,
            [
                this.createPointTopologyMapping(sourceStartPoint, arc.getStartPoint(), arc),
                this.createPointTopologyMapping(
                    sourceEndPoint,
                    secondArc?.getEndPoint() ?? arc.getEndPoint(),
                    secondArc ?? arc,
                ),
            ],
        ));
        affectedClosureSeeds.push(arc);

        if (this.options.keepTrimmedAsConstruction) {
            createdEntityIds.push(
                this.createArcSegmentFromDefinition(arc, sourceDefinition, trimmedRange, true).uuid,
            );
        }

        createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim(affectedClosureSeeds));

        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    /**
     * 应用整圆剪裁区间。
     * @param circle 目标圆。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applyCircleTrimInterval(
        circle: SketchCircleObject,
        trimInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(circle);
        const sourceCenterPoint = circle.getCenterControlPoint();
        const keptRange = this.geometryService.buildClosedCurveKeptRangeAfterTrim(trimInterval);
        const trimmedRange = this.geometryService.normalizeClosedTrimInterval(trimInterval.startT, trimInterval.endT);
        if (!keptRange || !trimmedRange) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds: [],
                removedEntityIds: [],
            };
        }

        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];

        const keptArc = this.createArcFromCircleRange(circle, keptRange, circle.isConstruction, circle.getCenterControlPoint());
        createdEntityIds.push(keptArc.uuid);

        if (this.options.keepTrimmedAsConstruction) {
            createdEntityIds.push(
                this.createArcFromCircleRange(circle, trimmedRange, true).uuid,
            );
        }

        const removedCircleIds = this.removeCurveEntity(circle);
        removedEntityIds.push(...removedCircleIds);
        if (removedCircleIds.length > 0) {
            // 圆裁剪为圆弧时，半径/圆心语义由唯一保留圆弧继承，尺寸身份保持不变。
            topologyChanges.push(this.createCurveTopologyChange(
                sourceIdentity,
                keptArc,
                [this.createPointTopologyMapping(sourceCenterPoint, keptArc.getCenterPoint(), keptArc)],
            ));
        }
        createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim([keptArc]));
        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    /**
     * 应用椭圆剪裁区间。
     * @param ellipse 目标椭圆。
     * @param isClosed 是否为完整闭合椭圆。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applyEllipseTrimInterval(
        ellipse: SketchEllipseObject,
        isClosed: boolean,
        trimInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(ellipse);
        const sourceCenterPoint = ellipse.getCenterPoint();
        const sourceStartPoint = ellipse.getStartPoint();
        const sourceEndPoint = ellipse.getEndPoint();
        const sourceDefinition = ellipse.getEllipseDefinition();
        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];

        if (isClosed) {
            const keptRange = this.geometryService.buildClosedCurveKeptRangeAfterTrim(trimInterval);
            const trimmedRange = this.geometryService.normalizeClosedTrimInterval(trimInterval.startT, trimInterval.endT);
            if (!keptRange || !trimmedRange) {
                return {
                    success: false,
                    message: '未生成有效剪裁段。',
                    createdEntityIds,
                    removedEntityIds,
                };
            }

            const keptEllipse = this.createEllipseSegmentFromDefinition(
                ellipse,
                sourceDefinition,
                keptRange,
                ellipse.isConstruction,
                ellipse.getCenterPoint(),
            );
            createdEntityIds.push(keptEllipse.uuid);
            if (this.options.keepTrimmedAsConstruction) {
                createdEntityIds.push(
                    this.createEllipseSegmentFromDefinition(ellipse, sourceDefinition, trimmedRange, true).uuid,
                );
            }

            const removedEllipseIds = this.removeCurveEntity(ellipse);
            removedEntityIds.push(...removedEllipseIds);
            if (removedEllipseIds.length > 0) {
                // 完整椭圆裁剪为部分椭圆时，中心和实体尺寸语义由唯一保留段继承。
                topologyChanges.push(this.createCurveTopologyChange(
                    sourceIdentity,
                    keptEllipse,
                    [this.createPointTopologyMapping(sourceCenterPoint, keptEllipse.getCenterPoint(), keptEllipse)],
                ));
            }
            // 完整椭圆裁剪后已经成为具有真实弧端点的部分椭圆，必须像整圆转圆弧一样触发混合闭合收口。
            createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim([keptEllipse]));
            this.viewer.requestRender();
            return {
                success: true,
                message: '剪裁完成。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        const {keptRanges, trimmedRange} = this.geometryService.buildOpenCurveRangesAfterTrim(trimInterval);
        if (!trimmedRange) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds,
                removedEntityIds: [],
            };
        }

        if (keptRanges.length === 0) {
            if (this.options.keepTrimmedAsConstruction) {
                ellipse.isConstruction = true;
                ellipse.updateEllipse();
                this.viewer.requestRender();
                return {
                    success: true,
                    message: '整段已保留为构造线。',
                    createdEntityIds,
                    removedEntityIds,
                    topologyChanges,
                };
            }

            const closureSeedsAfterRemoval = this.resolveClosureSeedsAfterWholeCurveRemoval(ellipse);
            const removalResult = this.removeCurveEntityWithTopology(ellipse);
            removedEntityIds.push(...removalResult.removedEntityIds);
            topologyChanges.push(...removalResult.topologyChanges);
            createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim(closureSeedsAfterRemoval));
            this.viewer.requestRender();
            return {
                success: true,
                message: '曲线已删除。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        const container = ellipse.parent;
        const collectionParent = isSketchCollectionObject(container) ? container : null;
        const wasClosedGeometry = collectionParent?.isClosedGeometry ?? false;
        const secondEllipse = keptRanges.length === 2
            ? this.createEllipseSegmentFromDefinition(ellipse, sourceDefinition, keptRanges[1], ellipse.isConstruction)
            : null;
        this.updateEllipseToRange(ellipse, sourceDefinition, keptRanges[0]);

        if (secondEllipse) {
            createdEntityIds.push(secondEllipse.uuid);
            this.attachSplitCurveToContainer(container, ellipse, secondEllipse);
        }

        if (collectionParent) {
            this.syncCollectionContourAfterTopologyMutation(collectionParent, wasClosedGeometry);
        }

        topologyChanges.push(this.createCurveTopologyChange(
            sourceIdentity,
            ellipse,
            [
                this.createPointTopologyMapping(sourceStartPoint, ellipse.getStartPoint(), ellipse),
                this.createPointTopologyMapping(
                    sourceEndPoint,
                    secondEllipse?.getEndPoint() ?? ellipse.getEndPoint(),
                    secondEllipse ?? ellipse,
                ),
            ],
        ));

        if (this.options.keepTrimmedAsConstruction) {
            createdEntityIds.push(
                this.createEllipseSegmentFromDefinition(ellipse, sourceDefinition, trimmedRange, true).uuid,
            );
        }
        createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim([
            ellipse,
            ...(secondEllipse ? [secondEllipse] : []),
        ]));

        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    //------ 样条精确裁剪 开始------
    /**
     * 应用抛物线剪裁区间。
     *
     * @param parabola 目标抛物线。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applyParabolaTrimInterval(
        parabola: SketchParabolaObject,
        trimInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(parabola);
        const sourceStartPoint = parabola.getStartPoint();
        const sourceEndPoint = parabola.getEndPoint();
        const sourceDefinition = parabola.getParabolaDefinition();
        const {keptRanges, trimmedRange} = this.geometryService.buildOpenCurveRangesAfterTrim(trimInterval);
        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];

        if (!trimmedRange) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds,
                removedEntityIds,
            };
        }

        if (keptRanges.length === 0) {
            if (this.options.keepTrimmedAsConstruction) {
                parabola.isConstruction = true;
                parabola.updateParabolaDefinition(sourceDefinition);
                this.viewer.requestRender();
                return {
                    success: true,
                    message: '整段已保留为构造线。',
                    createdEntityIds,
                    removedEntityIds,
                    topologyChanges,
                };
            }

            const removalResult = this.removeCurveEntityWithTopology(parabola);
            removedEntityIds.push(...removalResult.removedEntityIds);
            topologyChanges.push(...removalResult.topologyChanges);
            this.viewer.requestRender();
            return {
                success: true,
                message: '曲线已删除。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        const container = parabola.parent;
        const secondParabola = keptRanges.length === 2
            ? this.createParabolaSegmentFromDefinition(parabola, sourceDefinition, keptRanges[1], parabola.isConstruction)
            : null;
        this.updateParabolaToRange(parabola, sourceDefinition, keptRanges[0]);

        if (secondParabola) {
            createdEntityIds.push(secondParabola.uuid);
            this.attachSplitCurveToContainer(container, parabola, secondParabola);
        }

        topologyChanges.push(this.createCurveTopologyChange(
            sourceIdentity,
            parabola,
            [
                this.createPointTopologyMapping(sourceStartPoint, parabola.getStartPoint(), parabola),
                this.createPointTopologyMapping(
                    sourceEndPoint,
                    secondParabola?.getEndPoint() ?? parabola.getEndPoint(),
                    secondParabola ?? parabola,
                ),
            ],
        ));

        if (this.options.keepTrimmedAsConstruction) {
            createdEntityIds.push(
                this.createParabolaSegmentFromDefinition(parabola, sourceDefinition, trimmedRange, true).uuid,
            );
        }

        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    /**
     * 应用样条剪裁区间。
     *
     * @param curveData 剪裁前解析出的样条几何数据。
     * @param trimInterval 待剪裁区间。
     * @returns 操作结果。
     */
    private applySplineTrimInterval(
        curveData: SketchTrimCurveData,
        trimInterval: {startT: number; endT: number},
    ): SketchTrimOperationResult {
        const spline = curveData.curve as SketchSplineObject | SketchStyleSplineObject;
        const sourceIdentity = this.captureCurveIdentitySnapshot(spline);
        const sourceControlPointObjects = spline.getControlPoints();
        const sourceStartPoint = sourceControlPointObjects[0];
        const sourceEndPoint = sourceControlPointObjects[sourceControlPointObjects.length - 1];
        const sourceControlPoints = sourceControlPointObjects.map((point) => point.getPosition());
        const sourceMetadata = spline.getCurveMetadata();
        const openTrimResult = curveData.isClosed
            ? null
            : this.geometryService.buildOpenCurveRangesAfterTrim(trimInterval);
        const closedKeptRange = curveData.isClosed
            ? this.geometryService.buildClosedCurveKeptRangeAfterTrim(trimInterval)
            : null;
        const keptRanges = curveData.isClosed
            ? (closedKeptRange ? [closedKeptRange] : [])
            : (openTrimResult?.keptRanges ?? []);
        const trimmedRange = curveData.isClosed
            ? this.geometryService.normalizeClosedTrimInterval(trimInterval.startT, trimInterval.endT)
            : (openTrimResult?.trimmedRange ?? null);
        const createdEntityIds: string[] = [];
        const removedEntityIds: string[] = [];
        const topologyChanges: SketchDimensionTopologyChange[] = [];

        if (!trimmedRange) {
            return {
                success: false,
                message: '未生成有效剪裁段。',
                createdEntityIds,
                removedEntityIds,
            };
        }

        if (keptRanges.length === 0) {
            if (this.options.keepTrimmedAsConstruction) {
                spline.isConstruction = true;
                this.syncMaterialSnapshot(spline);
                this.viewer.requestRender();
                return {
                    success: true,
                    message: '整段已保留为构造线。',
                    createdEntityIds,
                    removedEntityIds,
                    topologyChanges,
                };
            }

            const removalResult = this.removeCurveEntityWithTopology(spline);
            removedEntityIds.push(...removalResult.removedEntityIds);
            topologyChanges.push(...removalResult.topologyChanges);
            this.viewer.requestRender();
            return {
                success: true,
                message: '曲线已删除。',
                createdEntityIds,
                removedEntityIds,
                topologyChanges,
            };
        }

        const container = spline.parent;
        if (curveData.isClosed) {
            // 自闭合样条被打开前必须释放旧轮廓；新的混合闭合区域稍后由统一拓扑服务重新识别。
            spline.clearContourDisplay();
        }

        const secondSpline = keptRanges.length === 2
            ? this.createSplineSegmentFromRange(
                curveData,
                sourceControlPoints,
                sourceMetadata,
                keptRanges[1],
                spline.isConstruction,
            )
            : null;

        if (curveData.isClosed || keptRanges.length === 2) {
            this.updateSplineToRange(curveData, sourceControlPoints, sourceMetadata, keptRanges[0]);
        } else {
            this.updateSplineToEdgeRange(curveData, sourceControlPoints, sourceMetadata, keptRanges[0]);
        }

        if (secondSpline) {
            createdEntityIds.push(secondSpline.uuid);
            this.attachSplitCurveToContainer(container, spline, secondSpline);
        }

        const targetControlPoints = spline.getControlPoints();
        const targetStartPoint = targetControlPoints[0];
        const secondControlPoints = secondSpline?.getControlPoints() ?? targetControlPoints;
        const targetEndPoint = secondControlPoints[secondControlPoints.length - 1];

        if (secondSpline) {
            // 一条样条拆成两个独立实体后，原整实体尺寸没有唯一继承目标，统一按拓扑失效处理。
            topologyChanges.push(this.createCurveTopologyChange(sourceIdentity, null));
        } else if (sourceStartPoint && sourceEndPoint && targetStartPoint && targetEndPoint) {
            topologyChanges.push(this.createCurveTopologyChange(
                sourceIdentity,
                spline,
                [
                    this.createPointTopologyMapping(sourceStartPoint, targetStartPoint, spline),
                    this.createPointTopologyMapping(sourceEndPoint, targetEndPoint, spline),
                ],
                ['spline-length'],
            ));
        }

        // 裁剪产生的新开放样条可与现有直线重新形成闭合区域，必须与本次裁剪归入同一事务。
        createdEntityIds.push(...this.tryFinalizeClosedCollectionsAfterTrim([
            spline,
            ...(secondSpline ? [secondSpline] : []),
        ]));

        if (this.options.keepTrimmedAsConstruction) {
            const constructionSpline = this.createSplineSegmentFromRange(
                curveData,
                sourceControlPoints,
                sourceMetadata,
                trimmedRange,
                true,
            );
            if (constructionSpline) {
                createdEntityIds.push(constructionSpline.uuid);
            }
        }

        this.viewer.requestRender();
        return {
            success: true,
            message: '剪裁完成。',
            createdEntityIds,
            removedEntityIds,
            topologyChanges,
        };
    }

    /**
     * 将抛物线更新到指定参数区间。
     *
     * @param parabola 目标抛物线。
     * @param sourceDefinition 原始抛物线定义。
     * @param range 保留参数区间。
     */
    private updateParabolaToRange(
        parabola: SketchParabolaObject,
        sourceDefinition: ReturnType<SketchParabolaObject['getParabolaDefinition']>,
        range: {startT: number; endT: number},
    ): void {
        parabola.updateParabolaDefinition(this.buildParabolaDefinitionFromRange(sourceDefinition, range));
        this.syncMaterialSnapshot(parabola.getFocalPoint());
        this.syncMaterialSnapshot(parabola.getApexPoint());
        this.syncMaterialSnapshot(parabola.getStartPoint());
        this.syncMaterialSnapshot(parabola.getEndPoint());
        this.syncMaterialSnapshot(parabola);
    }

    /**
     * 将样条更新到指定参数区间。
     *
     * @param curveData 剪裁前解析出的样条几何数据。
     * @param sourceControlPoints 原始控制点。
     * @param sourceMetadata 原始曲线元数据。
     * @param range 保留参数区间。
     */
    private updateSplineToRange(
        curveData: SketchTrimCurveData,
        sourceControlPoints: THREE.Vector3[],
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
        range: {startT: number; endT: number},
    ): void {
        const spline = curveData.curve as SketchSplineObject | SketchStyleSplineObject;
        const curvePoints = this.geometryService.collectCurvePointsForRange(curveData, range);
        const nextControlPoints = this.buildSplineControlPointsFromRange(
            curveData,
            sourceControlPoints,
            sourceMetadata,
            range,
            curvePoints,
        );
        this.rebuildSplineByControlPoints(spline, nextControlPoints, curvePoints, sourceMetadata);
        spline.getControlPoints().forEach((point) => {
            this.syncMaterialSnapshot(point);
        });
        this.syncMaterialSnapshot(spline);
    }

    /**
     * 将样条更新到边缘保留区间。
     *
     * 说明：
     * 1. 边缘裁剪仍沿原样条控制点提取 Bezier 子段，避免普通样条退化为两点直线；
     * 2. 被裁侧端点必须直接移动到交点，另一侧保持原端点拓扑；
     * 3. 这样可以让裁剪结果贴合交点，同时保留样条本身的弯曲控制能力。
     *
     * @param curveData 剪裁前解析出的样条几何数据。
     * @param sourceControlPoints 原始控制点。
     * @param sourceMetadata 原始曲线元数据。
     * @param range 保留参数区间。
     */
    private updateSplineToEdgeRange(
        curveData: SketchTrimCurveData,
        sourceControlPoints: THREE.Vector3[],
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
        range: {startT: number; endT: number},
    ): void {
        const spline = curveData.curve as SketchSplineObject | SketchStyleSplineObject;
        const tolerance = SketchTrimGeometryService.TOPOLOGY_TOLERANCE;
        const isTrimStartSide = range.startT > tolerance;
        const isTrimEndSide = range.endT < 1 - tolerance;
        if (isTrimStartSide === isTrimEndSide) {
            console.error(`[SketchTrimController] 边缘样条裁剪失败：保留区间未落在单侧边缘，样条=${spline.uuid}，startT=${range.startT}，endT=${range.endT}。`);
            return;
        }

        const curvePoints = this.geometryService.collectCurvePointsForRange(curveData, range);
        const nextControlPoints = this.buildSplineControlPointsFromRange(
            curveData,
            sourceControlPoints,
            sourceMetadata,
            range,
            curvePoints,
        );

        this.rebuildSplineByControlPoints(spline, nextControlPoints, curvePoints, sourceMetadata);
        spline.getControlPoints().forEach((point) => {
            this.syncMaterialSnapshot(point);
        });
        this.syncMaterialSnapshot(spline);
    }

    /**
     * 按参数区间创建抛物线对象。
     *
     * @param templateParabola 模板抛物线。
     * @param sourceDefinition 原始抛物线定义。
     * @param range 目标参数区间。
     * @param isConstruction 是否构造线。
     * @returns 新建抛物线对象。
     */
    private createParabolaSegmentFromDefinition(
        templateParabola: SketchParabolaObject,
        sourceDefinition: ReturnType<SketchParabolaObject['getParabolaDefinition']>,
        range: {startT: number; endT: number},
        isConstruction: boolean,
    ): SketchParabolaObject {
        const definition = this.buildParabolaDefinitionFromRange(sourceDefinition, range);
        const parabola = new SketchParabolaObject({
            material: isConstruction
                ? LineMaterialFactory.createConstructionLineMaterial()
                : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default),
            referencePlane: templateParabola.referencePlane,
            isConstruction,
            baseStyle: isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic,
            definition,
        });

        this.addObjectToSketchGroup(parabola);
        parabola.getControlPoints().forEach((point) => {
            this.addObjectToSketchGroup(point);
            this.syncMaterialSnapshot(point);
        });
        this.syncMaterialSnapshot(parabola);
        return parabola;
    }

    /**
     * 按参数区间创建样条对象。
     *
     * @param curveData 剪裁前解析出的样条几何数据。
     * @param sourceControlPoints 原始控制点。
     * @param sourceMetadata 原始曲线元数据。
     * @param range 目标参数区间。
     * @param isConstruction 是否构造线。
     * @returns 新建样条对象。
     */
    private createSplineSegmentFromRange(
        curveData: SketchTrimCurveData,
        sourceControlPoints: THREE.Vector3[],
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
        range: {startT: number; endT: number},
        isConstruction: boolean,
    ): SketchSplineObject | SketchStyleSplineObject | null {
        const templateSpline = curveData.curve as SketchSplineObject | SketchStyleSplineObject;
        const curvePoints = this.geometryService.collectCurvePointsForRange(curveData, range);
        const controlPointPositions = this.buildSplineControlPointsFromRange(
            curveData,
            sourceControlPoints,
            sourceMetadata,
            range,
            curvePoints,
        );
        const controlPoints = controlPointPositions.map((position) => this.createControlPointForPlane(position, templateSpline.referencePlane));
        const startPoint = controlPoints[0];
        const endPoint = controlPoints[controlPoints.length - 1];
        if (!startPoint || !endPoint || curvePoints.length < 2) {
            console.error(`[SketchTrimController] 创建样条裁剪段失败：曲线定义不足，样条=${templateSpline.uuid}，控制点数量=${controlPoints.length}，采样点数量=${curvePoints.length}。`);
            return null;
        }

        const sharedOptions = {
            material: isConstruction
                ? LineMaterialFactory.createConstructionLineMaterial()
                : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default),
            referencePlane: templateSpline.referencePlane,
            isConstruction,
            baseStyle: isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic,
            startPoint,
            endPoint,
            controlPoints: controlPoints.slice(1, -1),
            curvePoints,
            curveMetadata: this.buildTrimmedSplineMetadata(sourceMetadata, controlPointPositions.length),
        };
        const spline = isSketchStyleSplineObject(templateSpline)
            ? new SketchStyleSplineObject(sharedOptions)
            : new SketchSplineObject(sharedOptions);

        this.addObjectToSketchGroup(spline);
        spline.getControlPoints().forEach((point) => {
            this.syncMaterialSnapshot(point);
        });
        this.syncMaterialSnapshot(spline);
        return spline;
    }

    /**
     * 根据保留参数区间构建开放样条控制点，并强制让段两端落到求交交点。
     *
     * 说明：
     * 1. 开放 Bezier 可通过 De Casteljau 精确提取子段控制点；
     * 2. 插值样条、B 样条及跨接缝的闭合样条不能把端点直接当作两点定义，
     *    必须从原显示曲线采样出新的开放控制拓扑，避免保留段退化为弦线；
     * 3. 段首尾始终使用求交参数解析的真实交点，保证裁剪边界与相邻实体重合。
     *
     * @param curveData 剪裁前解析出的样条几何数据。
     * @param sourceControlPoints 原始控制点。
     * @param sourceMetadata 原始曲线元数据。
     * @param range 保留参数区间。
     * @param curvePoints 已从原曲线提取的连续采样点。
     * @returns 对齐交点后的控制点列表。
     */
    private buildSplineControlPointsFromRange(
        curveData: SketchTrimCurveData,
        sourceControlPoints: THREE.Vector3[],
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
        range: {startT: number; endT: number},
        curvePoints: THREE.Vector3[],
    ): THREE.Vector3[] {
        const startPoint = this.geometryService.resolveCurvePointAtParameter(curveData, range.startT);
        const endPoint = this.geometryService.resolveCurvePointAtParameter(curveData, range.endT);

        if (sourceControlPoints.length < 3 || curvePoints.length < 2) {
            // 两点样条没有可保留的弯曲控制点，只能以裁剪后的两端点作为最小合法定义。
            return [startPoint, endPoint];
        }

        const shouldExtractBezierRange = !curveData.isClosed
            && sourceMetadata.curveKind === SketchBezierCurveKind.Bezier;
        const nextControlPoints = shouldExtractBezierRange
            ? this.extractBezierControlPointRange(sourceControlPoints, range)
            : this.sampleSplineControlPointsFromCurvePoints(
                curvePoints,
                Math.max(3, sourceControlPoints.length),
            );

        nextControlPoints[0] = startPoint;
        nextControlPoints[nextControlPoints.length - 1] = endPoint;
        return nextControlPoints;
    }

    /**
     * 根据控制点重建样条。
     *
     * @param spline 目标样条。
     * @param controlPointPositions 新控制点坐标。
     * @param curvePoints 从原曲线参数区间提取的权威采样点。
     * @param sourceMetadata 原始曲线元数据。
     */
    private rebuildSplineByControlPoints(
        spline: SketchSplineObject | SketchStyleSplineObject,
        controlPointPositions: THREE.Vector3[],
        curvePoints: THREE.Vector3[],
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
    ): void {
        spline.rebuildByCurveDefinition({
            controlPointPositions,
            curvePoints,
            curveMetadata: this.buildTrimmedSplineMetadata(sourceMetadata, controlPointPositions.length),
        });
    }

    /**
     * 从保留曲线点列中均匀采样开放样条控制点。
     *
     * 设计说明：
     * 1. 闭合样条的保留区间可能跨越 `0/1` 接缝，无法使用开放 Bezier 的单区间拆分公式；
     * 2. 以源显示曲线为采样基线，可让当前裁剪结果保持原有曲率；
     * 3. 保持接近原控制点数量，为后续控制点编辑提供稳定且非退化的开放拓扑。
     *
     * @param curvePoints 连续曲线采样点。
     * @param preferredCount 期望控制点数量。
     * @returns 沿点列均匀插值得到的控制点。
     */
    private sampleSplineControlPointsFromCurvePoints(
        curvePoints: THREE.Vector3[],
        preferredCount: number,
    ): THREE.Vector3[] {
        if (curvePoints.length < 2) {
            return curvePoints.map((point) => point.clone());
        }

        const controlPointCount = Math.max(2, Math.floor(preferredCount));
        return Array.from({length: controlPointCount}, (_, index) => {
            const pointListParameter = index / (controlPointCount - 1) * (curvePoints.length - 1);
            const lowerIndex = Math.floor(pointListParameter);
            const upperIndex = Math.min(lowerIndex + 1, curvePoints.length - 1);
            const localParameter = pointListParameter - lowerIndex;
            return curvePoints[lowerIndex].clone().lerp(curvePoints[upperIndex], localParameter);
        });
    }

    /**
     * 构建裁剪后开放样条的元数据。
     *
     * @param sourceMetadata 原始曲线元数据。
     * @param controlPointCount 新控制点数量。
     * @returns 与新控制拓扑兼容的开放曲线元数据。
     */
    private buildTrimmedSplineMetadata(
        sourceMetadata: ReturnType<SketchSplineObject['getCurveMetadata']>,
        controlPointCount: number,
    ): ReturnType<SketchSplineObject['getCurveMetadata']> {
        const hasCompatibleWeights = sourceMetadata.weights.length === controlPointCount;
        const normalizedDegree = sourceMetadata.degree === null
            ? null
            : Math.min(sourceMetadata.degree, Math.max(1, controlPointCount - 1));
        return {
            ...sourceMetadata,
            closureKind: SketchBezierCurveClosureKind.Open,
            degree: normalizedDegree,
            weights: sourceMetadata.weights.length === 0
                ? []
                : hasCompatibleWeights
                    ? [...sourceMetadata.weights]
                    : new Array(controlPointCount).fill(1),
            isRational: sourceMetadata.isRational && hasCompatibleWeights,
        };
    }

    /**
     * 从 Bezier 曲线中精确提取参数子段控制点。
     *
     * @param sourceControlPoints 原始控制点。
     * @param range 参数区间。
     * @returns 子段控制点。
     */
    private extractBezierControlPointRange(
        sourceControlPoints: THREE.Vector3[],
        range: {startT: number; endT: number},
    ): THREE.Vector3[] {
        const startT = this.clamp01(range.startT);
        const endT = this.clamp01(range.endT);
        if (endT <= startT) {
            console.error(`[SketchTrimController] 提取样条裁剪段失败：参数区间非法，startT=${range.startT}，endT=${range.endT}。`);
            return sourceControlPoints.map((point) => point.clone());
        }

        const [, rightPart] = this.splitBezierControlPoints(sourceControlPoints, startT);
        const localEndT = (endT - startT) / (1 - startT);
        const [segmentControlPoints] = this.splitBezierControlPoints(rightPart, localEndT);
        return segmentControlPoints;
    }

    /**
     * 使用 De Casteljau 算法按参数拆分 Bezier 控制点。
     *
     * @param controlPoints 原始控制点。
     * @param parameterT 拆分参数。
     * @returns 左右两段控制点。
     */
    private splitBezierControlPoints(controlPoints: THREE.Vector3[], parameterT: number): [THREE.Vector3[], THREE.Vector3[]] {
        if (controlPoints.length < 2) {
            console.error(`[SketchTrimController] 拆分样条失败：控制点数量不足，控制点数量=${controlPoints.length}。`);
            const fallbackPoint = controlPoints[0]?.clone() ?? new THREE.Vector3();
            return [
                [fallbackPoint.clone(), fallbackPoint.clone()],
                [fallbackPoint.clone(), fallbackPoint.clone()],
            ];
        }

        const t = this.clamp01(parameterT);
        const levels: THREE.Vector3[][] = [controlPoints.map((point) => point.clone())];
        while (levels[levels.length - 1].length > 1) {
            const previousLevel = levels[levels.length - 1];
            levels.push(previousLevel.slice(0, -1).map((point, index) => point.clone().lerp(previousLevel[index + 1], t)));
        }

        const leftControlPoints = levels.map((level) => level[0].clone());
        const rightControlPoints = levels.map((level) => level[level.length - 1].clone()).reverse();
        return [leftControlPoints, rightControlPoints];
    }

    /**
     * 按参数区间构造抛物线定义。
     *
     * @param sourceDefinition 原始定义。
     * @param range 参数区间。
     * @returns 新定义。
     */
    private buildParabolaDefinitionFromRange(
        sourceDefinition: ReturnType<SketchParabolaObject['getParabolaDefinition']>,
        range: {startT: number; endT: number},
    ): ReturnType<SketchParabolaObject['getParabolaDefinition']> {
        const startParameter = THREE.MathUtils.lerp(
            sourceDefinition.startParameter,
            sourceDefinition.endParameter,
            this.clamp01(range.startT),
        );
        const endParameter = THREE.MathUtils.lerp(
            sourceDefinition.startParameter,
            sourceDefinition.endParameter,
            this.clamp01(range.endT),
        );
        return {
            ...sourceDefinition,
            startParameter,
            endParameter,
        };
    }

    /**
     * 将数值限制在归一化参数范围内。
     *
     * @param value 原始数值。
     * @returns `[0, 1]` 范围内的数值。
     */
    private clamp01(value: number): number {
        if (!Number.isFinite(value)) {
            return 0;
        }
        return THREE.MathUtils.clamp(value, 0, 1);
    }
    //------ 样条精确裁剪 结束------

    /**
     * 将圆弧更新到指定参数区间。
     * @param arc 目标圆弧。
     * @param sourceDefinition 原始定义。
     * @param range 目标参数区间。
     */
    private updateArcToRange(
        arc: SketchArcObject,
        sourceDefinition: ReturnType<SketchArcObject['getArcDefinition']>,
        range: {startT: number; endT: number},
    ): void {
        arc.updateArcDefinition(this.buildArcDefinitionFromRange(sourceDefinition, range));
        arc.syncControlPointsFromDefinition();
        this.syncMaterialSnapshot(arc.getStartPoint());
        this.syncMaterialSnapshot(arc.getEndPoint());
        this.syncMaterialSnapshot(arc.getCenterPoint());
        this.syncMaterialSnapshot(arc);
    }

    /**
     * 将部分椭圆更新到指定参数区间。
     * @param ellipse 目标椭圆。
     * @param sourceDefinition 原始定义。
     * @param range 目标参数区间。
     */
    private updateEllipseToRange(
        ellipse: SketchEllipseObject,
        sourceDefinition: ReturnType<SketchEllipseObject['getEllipseDefinition']>,
        range: {startT: number; endT: number},
    ): void {
        ellipse.updateEllipseDefinition(this.buildEllipseDefinitionFromRange(sourceDefinition, range));
        ellipse.syncControlPointsFromDefinition();
        this.syncMaterialSnapshot(ellipse.getStartPoint());
        this.syncMaterialSnapshot(ellipse.getEndPoint());
        this.syncMaterialSnapshot(ellipse.getCenterPoint());
        this.syncMaterialSnapshot(ellipse);
    }

    /**
     * 按参数区间创建圆弧对象。
     * @param templateArc 模板圆弧。
     * @param sourceDefinition 原始圆弧定义。
     * @param range 目标参数区间。
     * @param isConstruction 是否构造线。
     * @returns 新建圆弧对象。
     */
    private createArcSegmentFromDefinition(
        templateArc: SketchArcObject,
        sourceDefinition: ReturnType<SketchArcObject['getArcDefinition']>,
        range: {startT: number; endT: number},
        isConstruction: boolean,
    ): SketchArcObject {
        const definition = this.buildArcDefinitionFromRange(sourceDefinition, range);
        const arc = new SketchArcObject({
            material: isConstruction
                ? LineMaterialFactory.createConstructionLineMaterial()
                : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default),
            referencePlane: templateArc.referencePlane,
            isConstruction,
            baseStyle: isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic,
            definition,
            startPoint: this.createControlPointForPlane(
                this.resolveCircleWorldPoint(templateArc.referencePlane, definition.centerWorld, definition.radius, definition.startAngle),
                templateArc.referencePlane,
            ),
            endPoint: this.createControlPointForPlane(
                this.resolveCircleWorldPoint(templateArc.referencePlane, definition.centerWorld, definition.radius, definition.endAngle),
                templateArc.referencePlane,
            ),
            centerPoint: this.createControlPointForPlane(definition.centerWorld, templateArc.referencePlane),
        });

        this.addObjectToSketchGroup(arc);
        this.syncMaterialSnapshot(arc.getStartPoint());
        this.syncMaterialSnapshot(arc.getEndPoint());
        this.syncMaterialSnapshot(arc.getCenterPoint());
        this.syncMaterialSnapshot(arc);
        return arc;
    }

    /**
     * 按参数区间从整圆创建圆弧对象。
     * @param circle 模板圆。
     * @param range 目标参数区间。
     * @param isConstruction 是否构造线。
     * @param reusedCenterPoint 可复用的圆心控制点。
     * @returns 新建圆弧对象。
     */
    private createArcFromCircleRange(
        circle: SketchCircleObject,
        range: {startT: number; endT: number},
        isConstruction: boolean,
        reusedCenterPoint?: SketchPointObject,
    ): SketchArcObject {
        const definition = this.buildCircleArcDefinitionFromRange(circle, range);
        const arc = new SketchArcObject({
            material: isConstruction
                ? LineMaterialFactory.createConstructionLineMaterial()
                : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default),
            referencePlane: circle.referencePlane,
            isConstruction,
            baseStyle: isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic,
            definition,
            startPoint: this.createControlPointForPlane(
                this.resolveCircleWorldPoint(circle.referencePlane, definition.centerWorld, definition.radius, definition.startAngle),
                circle.referencePlane,
            ),
            endPoint: this.createControlPointForPlane(
                this.resolveCircleWorldPoint(circle.referencePlane, definition.centerWorld, definition.radius, definition.endAngle),
                circle.referencePlane,
            ),
            centerPoint: reusedCenterPoint ?? this.createControlPointForPlane(definition.centerWorld, circle.referencePlane),
        });

        this.addObjectToSketchGroup(arc);
        this.syncMaterialSnapshot(arc.getStartPoint());
        this.syncMaterialSnapshot(arc.getEndPoint());
        this.syncMaterialSnapshot(arc.getCenterPoint());
        this.syncMaterialSnapshot(arc);
        return arc;
    }

    /**
     * 按参数区间创建部分椭圆对象。
     * @param templateEllipse 模板椭圆。
     * @param sourceDefinition 原始椭圆定义。
     * @param range 目标参数区间。
     * @param isConstruction 是否构造线。
     * @param reusedCenterPoint 可复用的中心点对象。
     * @returns 新建椭圆对象。
     */
    private createEllipseSegmentFromDefinition(
        templateEllipse: SketchEllipseObject,
        sourceDefinition: ReturnType<SketchEllipseObject['getEllipseDefinition']>,
        range: {startT: number; endT: number},
        isConstruction: boolean,
        reusedCenterPoint?: SketchPointObject,
    ): SketchEllipseObject {
        const definition = this.buildEllipseDefinitionFromRange(sourceDefinition, range);
        const startWorld = this.ellipseGeometryService.evaluateWorldPointAtAngle(
            templateEllipse.referencePlane,
            definition,
            definition.startAngle,
        );
        const endWorld = this.ellipseGeometryService.evaluateWorldPointAtAngle(
            templateEllipse.referencePlane,
            definition,
            definition.endAngle,
        );
        const controlPointFactory = new SketchEllipseControlPointFactory(templateEllipse.referencePlane);
        const startPoint = controlPointFactory.createControlPoint(startWorld);
        const endPoint = controlPointFactory.createControlPoint(endWorld);
        const centerPoint = reusedCenterPoint ?? controlPointFactory.createControlPoint(definition.centerWorld);
        const axisControlPoints = controlPointFactory.createAxisControlPoints({
            centerWorld: definition.centerWorld,
            radiusX: definition.radiusX,
            radiusY: definition.radiusY,
            rotation: definition.rotation,
        });
        const ellipse = new SketchEllipseObject({
            material: isConstruction
                ? LineMaterialFactory.createConstructionLineMaterial()
                : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default),
            referencePlane: templateEllipse.referencePlane,
            isConstruction,
            baseStyle: isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic,
            definition,
            startPoint,
            endPoint,
            centerPoint,
        });
        controlPointFactory.registerCardinalControlPoints(ellipse, {
            centerPoint,
            axisControlPoints,
        });

        ellipse.updateEllipse();
        this.addObjectToSketchGroup(ellipse);
        [
            ellipse.getStartPoint(),
            ellipse.getEndPoint(),
            ...controlPointFactory.collectRegisteredControlPoints({
                centerPoint,
                axisControlPoints,
            }),
        ].forEach((point) => {
            this.addObjectToSketchGroup(point);
            this.syncMaterialSnapshot(point);
        });
        this.syncMaterialSnapshot(ellipse);
        return ellipse;
    }

    /**
     * 将拆分得到的新曲线挂入与原曲线一致的容器。
     * @param container 原父容器。
     * @param original 原曲线。
     * @param created 新曲线。
     */
    private attachSplitCurveToContainer(
        container: THREE.Object3D | null,
        original: SketchTrimCurveObject,
        created: SketchTrimCurveObject,
    ): void {
        if (!container) {
            this.addObjectToSketchGroup(created);
            return;
        }

        if (isSketchCollectionObject(container)) {
            this.replaceLineInCollection(container, original, [original, created]);
            return;
        }

        if (created.parent !== container) {
            this.viewer.addObject(created, container);
        }
    }

    /**
     * 按参数区间构造圆弧定义。
     * @param sourceDefinition 原始定义。
     * @param range 参数区间。
     * @returns 新定义。
     */
    private buildArcDefinitionFromRange(
        sourceDefinition: ReturnType<SketchArcObject['getArcDefinition']>,
        range: {startT: number; endT: number},
    ): ReturnType<SketchArcObject['getArcDefinition']> {
        return {
            ...sourceDefinition,
            startAngle: this.resolveAngleByParameter(
                sourceDefinition.startAngle,
                sourceDefinition.endAngle,
                sourceDefinition.clockwise,
                range.startT,
            ),
            endAngle: this.resolveAngleByParameter(
                sourceDefinition.startAngle,
                sourceDefinition.endAngle,
                sourceDefinition.clockwise,
                range.endT,
            ),
        };
    }

    /**
     * 按参数区间构造整圆转圆弧定义。
     * @param circle 模板圆。
     * @param range 参数区间。
     * @returns 圆弧定义。
     */
    private buildCircleArcDefinitionFromRange(
        circle: SketchCircleObject,
        range: {startT: number; endT: number},
    ): ReturnType<SketchArcObject['getArcDefinition']> {
        return {
            centerWorld: circle.getCenterWorld(),
            radius: circle.getRadius(),
            startAngle: range.startT * Math.PI * 2,
            endAngle: range.endT * Math.PI * 2,
            clockwise: false,
            segments: 64,
        };
    }

    /**
     * 按参数区间构造椭圆定义。
     * @param sourceDefinition 原始定义。
     * @param range 参数区间。
     * @returns 新定义。
     */
    private buildEllipseDefinitionFromRange(
        sourceDefinition: ReturnType<SketchEllipseObject['getEllipseDefinition']>,
        range: {startT: number; endT: number},
    ): ReturnType<SketchEllipseObject['getEllipseDefinition']> {
        return {
            ...sourceDefinition,
            startAngle: this.ellipseGeometryService.resolveAngleAtParameter(sourceDefinition, range.startT),
            endAngle: this.ellipseGeometryService.resolveAngleAtParameter(sourceDefinition, range.endT),
        };
    }

    /**
     * 按归一化参数解析圆弧实际角度。
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @param parameterT 参数。
     * @returns 圆弧角度值。
     */
    private resolveAngleByParameter(
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
        parameterT: number,
    ): number {
        const sweep = this.resolveAngleSweep(startAngle, endAngle, clockwise);
        return clockwise
            ? startAngle - sweep * parameterT
            : startAngle + sweep * parameterT;
    }

    /**
     * 解析圆弧正向扫掠量。
     * @param startAngle 起始角。
     * @param endAngle 终止角。
     * @param clockwise 是否顺时针。
     * @returns 正向扫掠角度。
     */
    private resolveAngleSweep(startAngle: number, endAngle: number, clockwise: boolean): number {
        let sweep = clockwise ? startAngle - endAngle : endAngle - startAngle;
        while (sweep <= 0) {
            sweep += Math.PI * 2;
        }
        return sweep;
    }

    /**
     * 解析圆周上指定角度的世界坐标。
     * @param referencePlane 参考面。
     * @param centerWorld 圆心世界坐标。
     * @param radius 半径。
     * @param angle 角度。
     * @returns 世界坐标。
     */
    private resolveCircleWorldPoint(
        referencePlane: SketchArcObject['referencePlane'] | SketchCircleObject['referencePlane'],
        centerWorld: THREE.Vector3,
        radius: number,
        angle: number,
    ): THREE.Vector3 {
        const localCenter = referencePlane.worldToLocal(centerWorld);
        return referencePlane.localToWorld(new THREE.Vector2(
            localCenter.x + radius * Math.cos(angle),
            localCenter.y + radius * Math.sin(angle),
        ));
    }

    /**
     * 捕获线段状态快照。
     * @param line 目标线。
     * @returns 线段状态快照。
     */
    private captureLineSnapshot(line: SketchLineObject): SketchTrimLineSnapshot {
        const start = line.startPoint.getPosition();
        const end = line.endPoint.getPosition();
        return {
            curveKind: SketchTrimCurveKind.Line,
            line,
            startPoint: line.startPoint,
            endPoint: line.endPoint,
            startPosition: [start.x, start.y, start.z],
            endPosition: [end.x, end.y, end.z],
            isConstruction: line.isConstruction,
        };
    }

    /**
     * 捕获曲线状态快照。
     * @param curve 目标曲线。
     * @returns 曲线状态快照。
     */
    private captureCurveSnapshot(curve: SketchTrimCurveObject): SketchTrimUpdatedCurveSnapshot | null {
        if (isSketchLineObject(curve)) {
            return this.captureLineSnapshot(curve);
        }

        if (isSketchArcObject(curve)) {
            const start = curve.getStartPoint().getPosition();
            const end = curve.getEndPoint().getPosition();
            const center = curve.getCenterPoint().getPosition();
            return {
                curveKind: SketchTrimCurveKind.Arc,
                line: curve,
                startPoint: curve.getStartPoint(),
                endPoint: curve.getEndPoint(),
                centerPoint: curve.getCenterPoint(),
                startPosition: [start.x, start.y, start.z],
                endPosition: [end.x, end.y, end.z],
                centerPosition: [center.x, center.y, center.z],
                definition: curve.getArcDefinition(),
                isConstruction: curve.isConstruction,
            };
        }

        //------ 强力剪裁圆撤销快照 开始------
        if (isSketchCircleObject(curve)) {
            const center = curve.getCenterControlPoint().getPosition();
            const edge = curve.getEdgeControlPoint().getPosition();
            return {
                curveKind: SketchTrimCurveKind.Circle,
                line: curve,
                startPoint: curve.getCenterControlPoint(),
                endPoint: curve.getEdgeControlPoint(),
                startPosition: [center.x, center.y, center.z],
                endPosition: [edge.x, edge.y, edge.z],
                radius: curve.getRadius(),
                isConstruction: curve.isConstruction,
            };
        }
        //------ 强力剪裁圆撤销快照 结束------

        if (isSketchEllipseObject(curve)) {
            const start = curve.getStartPoint().getPosition();
            const end = curve.getEndPoint().getPosition();
            const center = curve.getCenterPoint().getPosition();
            return {
                curveKind: SketchTrimCurveKind.Ellipse,
                line: curve,
                startPoint: curve.getStartPoint(),
                endPoint: curve.getEndPoint(),
                centerPoint: curve.getCenterPoint(),
                startPosition: [start.x, start.y, start.z],
                endPosition: [end.x, end.y, end.z],
                centerPosition: [center.x, center.y, center.z],
                definition: curve.getEllipseDefinition(),
                isConstruction: curve.isConstruction,
            };
        }

        //------ 样条精确裁剪 开始------
        if (isSketchParabolaObject(curve)) {
            return {
                curveKind: SketchTrimCurveKind.Parabola,
                line: curve,
                definition: curve.getParabolaDefinition(),
                isConstruction: curve.isConstruction,
            };
        }

        if (isSketchSplineObject(curve) || isSketchStyleSplineObject(curve)) {
            return {
                curveKind: isSketchStyleSplineObject(curve)
                    ? SketchTrimCurveKind.StyleSpline
                    : SketchTrimCurveKind.Spline,
                line: curve,
                controlPointPositions: curve.getControlPoints().map((point) => {
                    const position = point.getPosition();
                    return [position.x, position.y, position.z] as [number, number, number];
                }),
                curvePointPositions: curve.getCurvePoints().map((point) => [point.x, point.y, point.z] as [number, number, number]),
                curveMetadata: curve.getCurveMetadata(),
                isConstruction: curve.isConstruction,
            };
        }
        //------ 样条精确裁剪 结束------

        console.error(`[SketchTrimController] 当前曲线类型暂不支持更新快照捕获：${(curve as {uuid?: string}).uuid ?? '未知'}。`);
        return null;
    }

    /**
     * 捕获线段删除恢复快照。
     * @param line 目标线。
     * @returns 删除恢复快照。
     */
    private captureRemovedCurveSnapshot(line: SketchTrimCurveObject): SketchTrimRemovedLineSnapshot | null {
        const parent = line.parent;
        if (!parent) {
            console.error(`[SketchTrimController] 捕获删除快照失败：目标曲线未挂载到场景，曲线=${line.uuid}。`);
            return null;
        }

        //------ Trim 构造线圆删除快照兼容 开始------
        let parentKind = isSketchCollectionObject(parent)
            ? SketchTrimRemovedLineParentKind.Collection
            : SketchTrimRemovedLineParentKind.Object3D;
        let collectionIndex = isSketchCollectionObject(parent)
            ? Array.from(parent.collectSet).findIndex((source) => source.uuid === line.uuid)
            : null;

        if (isSketchCollectionObject(parent) && collectionIndex === -1) {
            const isDirectCollectionChild = parent.children.some((child) => child.uuid === line.uuid);
            if (!isDirectCollectionChild) {
                console.error(`[SketchTrimController] 捕获删除快照失败：集合中未找到目标曲线，集合=${parent.uuid}，曲线=${line.uuid}。`);
                return null;
            }

            // 多边形辅助圆等构造曲线会直接挂在集合 children 下，但不属于 collectSet。
            // 这类对象恢复时应按普通子节点回挂，而不是走集合源重建链路。
            parentKind = SketchTrimRemovedLineParentKind.Object3D;
            collectionIndex = null;
        }
        //------ Trim 构造线圆删除快照兼容 结束------

        return {
            curveKind: this.resolveCurveKind(line),
            line,
            parent,
            parentKind,
            collectionIndex,
            pointSnapshots: line.getRelatedSketchPointsSnapshot().map((point) => ({
                point,
                parent: point.parent,
            })),
        };
    }

    /**
     * 恢复线段快照。
     * @param snapshot 线段快照。
     */
    private restoreLineSnapshot(snapshot: SketchTrimLineSnapshot): void {
        const line = snapshot.line;
        const nextStart = new THREE.Vector3(
            snapshot.startPosition[0],
            snapshot.startPosition[1],
            snapshot.startPosition[2],
        );
        const nextEnd = new THREE.Vector3(
            snapshot.endPosition[0],
            snapshot.endPosition[1],
            snapshot.endPosition[2],
        );

        snapshot.startPoint.setPosition(nextStart);
        snapshot.endPoint.setPosition(nextEnd);
        line.setEndpoints(snapshot.startPoint, snapshot.endPoint);
        line.isConstruction = snapshot.isConstruction;
        line.updateGeometry();

        this.syncMaterialSnapshot(snapshot.startPoint);
        this.syncMaterialSnapshot(snapshot.endPoint);
        this.syncMaterialSnapshot(line);
    }

    /**
     * 恢复曲线快照。
     * @param snapshot 曲线快照。
     */
    private restoreCurveSnapshot(snapshot: SketchTrimUpdatedCurveSnapshot): void {
        if (snapshot.curveKind === SketchTrimCurveKind.Line) {
            this.restoreLineSnapshot(snapshot);
            return;
        }

        if (snapshot.curveKind === SketchTrimCurveKind.Arc) {
            snapshot.line.updateArcDefinition(snapshot.definition);
            snapshot.line.syncControlPointsFromDefinition();
            snapshot.line.isConstruction = snapshot.isConstruction;
            this.syncMaterialSnapshot(snapshot.startPoint);
            this.syncMaterialSnapshot(snapshot.endPoint);
            this.syncMaterialSnapshot(snapshot.centerPoint);
            this.syncMaterialSnapshot(snapshot.line);
            return;
        }

        //------ 强力剪裁圆撤销快照 开始------
        if (snapshot.curveKind === SketchTrimCurveKind.Circle) {
            const nextCenter = new THREE.Vector3(
                snapshot.startPosition[0],
                snapshot.startPosition[1],
                snapshot.startPosition[2],
            );

            // 圆对象以“圆心 + 半径”作为单一几何真源，撤销时先还原圆心再还原半径。
            snapshot.startPoint.syncPositionSilently(nextCenter);
            snapshot.line.setRadiusSilently(snapshot.radius);
            snapshot.line.isConstruction = snapshot.isConstruction;
            this.syncMaterialSnapshot(snapshot.startPoint);
            this.syncMaterialSnapshot(snapshot.endPoint);
            this.syncMaterialSnapshot(snapshot.line);
            return;
        }
        //------ 强力剪裁圆撤销快照 结束------

        //------ 样条精确裁剪 开始------
        if (
            snapshot.curveKind === SketchTrimCurveKind.Spline
            || snapshot.curveKind === SketchTrimCurveKind.StyleSpline
        ) {
            const controlPointPositions = snapshot.controlPointPositions.map(
                (position) => new THREE.Vector3(position[0], position[1], position[2]),
            );
            const curvePoints = snapshot.curvePointPositions.map(
                (position) => new THREE.Vector3(position[0], position[1], position[2]),
            );
            snapshot.line.rebuildByCurveDefinition({
                controlPointPositions,
                curvePoints,
                curveMetadata: snapshot.curveMetadata,
            });
            snapshot.line.isConstruction = snapshot.isConstruction;
            snapshot.line.getControlPoints().forEach((point) => {
                this.syncMaterialSnapshot(point);
            });
            this.syncMaterialSnapshot(snapshot.line);
            return;
        }
        //------ 样条精确裁剪 结束------

        if (snapshot.curveKind === SketchTrimCurveKind.Ellipse) {
            snapshot.line.updateEllipseDefinition(snapshot.definition);
            snapshot.line.syncControlPointsFromDefinition();
            snapshot.line.isConstruction = snapshot.isConstruction;
            this.syncMaterialSnapshot(snapshot.startPoint);
            this.syncMaterialSnapshot(snapshot.endPoint);
            this.syncMaterialSnapshot(snapshot.centerPoint);
            this.syncMaterialSnapshot(snapshot.line);
            return;
        }

        //------ 样条精确裁剪 开始------
        if (snapshot.curveKind === SketchTrimCurveKind.Parabola) {
            snapshot.line.updateParabolaDefinition(snapshot.definition);
            snapshot.line.isConstruction = snapshot.isConstruction;
            snapshot.line.getControlPoints().forEach((point) => {
                this.syncMaterialSnapshot(point);
            });
            this.syncMaterialSnapshot(snapshot.line);
        }
        //------ 样条精确裁剪 结束------
    }

    /**
     * 恢复被删除线段。
     * @param snapshot 删除快照。
     */
    private restoreRemovedCurve(snapshot: SketchTrimRemovedLineSnapshot): void {
        const legacySnapshot = snapshot as SketchTrimRemovedLineSnapshot & {
            startPoint?: SketchPointObject;
            endPoint?: SketchPointObject;
            startPointParent?: THREE.Object3D | null;
            endPointParent?: THREE.Object3D | null;
        };
        const pointSnapshots = snapshot.pointSnapshots ?? [
            legacySnapshot.startPoint
                ? {point: legacySnapshot.startPoint, parent: legacySnapshot.startPointParent ?? null}
                : null,
            legacySnapshot.endPoint
                ? {point: legacySnapshot.endPoint, parent: legacySnapshot.endPointParent ?? null}
                : null,
        ].filter((item): item is {point: SketchPointObject; parent: THREE.Object3D | null} => item !== null);

        pointSnapshots.forEach((pointSnapshot) => {
            if (pointSnapshot.parent && pointSnapshot.point.parent !== pointSnapshot.parent) {
                this.viewer.addObject(pointSnapshot.point, pointSnapshot.parent);
            }
        });

        if (snapshot.parentKind === SketchTrimRemovedLineParentKind.Collection) {
            if (!isSketchCollectionObject(snapshot.parent)) {
                console.error(`[SketchTrimController] 恢复曲线失败：父容器类型与快照不一致，曲线=${snapshot.line.uuid}，父容器=${snapshot.parent.uuid}。`);
                return;
            }
            this.restoreRemovedLineIntoCollection(snapshot.parent, snapshot.line, snapshot.collectionIndex);
        } else if (snapshot.line.parent !== snapshot.parent) {
            this.viewer.addObject(snapshot.line, snapshot.parent);
        }

        pointSnapshots.forEach((pointSnapshot) => {
            this.syncMaterialSnapshot(pointSnapshot.point);
        });
        this.syncMaterialSnapshot(snapshot.line);
    }

    /**
     * 将被删除线段按原索引恢复到集合中。
     * @param collection 集合对象。
     * @param line 线对象。
     * @param index 目标索引。
     */
    private restoreRemovedLineIntoCollection(
        collection: SketchCollectionObject,
        line: SketchTrimCurveObject,
        index: number | null,
    ): void {
        const wasClosedGeometry = collection.isClosedGeometry;
        const sources = Array.from(collection.collectSet);
        if (sources.some((source) => source.uuid === line.uuid)) {
            return;
        }

        const normalizedIndex = index === null
            ? sources.length
            : Math.min(Math.max(index, 0), sources.length);
        const nextSources = [
            ...sources.slice(0, normalizedIndex),
            line,
            ...sources.slice(normalizedIndex),
        ];
        collection.setSources(nextSources as CollectSource[]);
        this.syncCollectionContourAfterTopologyMutation(collection, wasClosedGeometry);
    }

    //------ 强力剪裁撤销能力 结束------

    /**
     * 根据目标线收集边界参数。
     * @param lineData 目标线数据。
     * @param includeTargetOutside 是否允许目标参数越界。
     * @returns 参数列表。
     */
    private collectBoundaryParametersForLine(lineData: SketchTrimLineData, includeTargetOutside: boolean): number[] {
        const candidates = this.collectLineDataCandidates(lineData.line.uuid);
        return this.geometryService.collectBoundaryParameters(lineData, candidates, {
            includeEndpoints: !includeTargetOutside,
            includeTargetOutside,
            includeSourceOutside: false,
        });
    }

    /**
     * 根据目标曲线收集单曲线剪裁边界参数。
     *
     * 开放曲线将天然端点作为边界，闭合曲线只使用真实交点，确保 Power Trim 与
     * TrimToClosest 对同一点击位置采用一致的区段划分规则。
     *
     * @param curveData 目标曲线数据。
     * @returns 参数列表。
     */
    private collectBoundaryParametersForCurve(curveData: SketchTrimCurveData): number[] {
        const candidates = this.collectCurveDataCandidates(curveData.curve.uuid);
        return this.geometryService.collectCurveBoundaryParameters(
            curveData,
            candidates,
            !curveData.isClosed,
        );
    }

    /**
     * 收集当前 Trim 曲线集合中的全部真实交点。
     * @param curveDataList 曲线数据列表。
     * @returns 去重后的交点列表。
     */
    private collectAllTrimIntersectionPoints(curveDataList: SketchTrimCurveData[]): THREE.Vector3[] {
        const intersectionPoints: THREE.Vector3[] = [];

        for (let targetIndex = 0; targetIndex < curveDataList.length; targetIndex += 1) {
            const targetCurveData = curveDataList[targetIndex];

            for (let candidateIndex = targetIndex + 1; candidateIndex < curveDataList.length; candidateIndex += 1) {
                const candidateCurveData = curveDataList[candidateIndex];
                const intersectionParameters = this.geometryService.collectCurveBoundaryParameters(
                    targetCurveData,
                    [candidateCurveData],
                    false,
                );

                intersectionParameters.forEach((parameterT) => {
                    const point = this.geometryService.resolveCurvePointAtIntersectionParameter(targetCurveData, parameterT);
                    if (intersectionPoints.some((candidate) => candidate.distanceToSquared(point) <= 1e-8)) {
                        return;
                    }
                    intersectionPoints.push(point);
                });
            }
        }

        return intersectionPoints;
    }

    /**
     * 创建单个 Trim 交点调试圆形精灵。
     * @param point 交点世界坐标。
     */
    private createDebugIntersectionSprite(point: THREE.Vector3): void {
        const material = PointsMaterialFactory.getSketchControlPointSpriteMaterial().clone();
        material.name = 'SketchTrimDebugIntersectionSpriteMaterial';

        const debugSprite = new THREE.Sprite(material);
        debugSprite.name = 'SketchTrimDebugIntersectionSprite';
        debugSprite.position.copy(point);
        debugSprite.scale.set(
            SketchTrimController.DEBUG_INTERSECTION_SPRITE_SIZE,
            SketchTrimController.DEBUG_INTERSECTION_SPRITE_SIZE,
            1,
        );
        debugSprite.renderOrder = 998;
        debugSprite.userData.skipSelection = true;
        debugSprite.userData.isSketchTrimDebugIntersectionSprite = true;
        debugSprite.raycast = (): void => {};

        this.viewer.scene.add(debugSprite);
        this.debugIntersectionSprites.push(debugSprite);
    }

    /**
     * 收集场景线段候选。
     * @param excludedLineId 排除线 ID。
     * @returns 线段数据列表。
     */
    private collectLineDataCandidates(excludedLineId: string): SketchTrimLineData[] {
        const lineCandidates = this.collectLineCandidates(this.options.ignoreConstructionGeometry);

        const lineDataList: SketchTrimLineData[] = [];
        lineCandidates.forEach((candidate) => {
            if (candidate.uuid === excludedLineId) {
                return;
            }
            lineDataList.push(this.geometryService.resolveLineData(candidate));
        });

        return lineDataList;
    }

    /**
     * 收集场景强力剪裁曲线候选。
     * @param excludedCurveId 排除曲线 ID。
     * @returns 曲线数据列表。
     */
    private collectCurveDataCandidates(excludedCurveId: string): SketchTrimCurveData[] {
        const curveCandidates = this.collectPowerTrimCurveCandidates(this.options.ignoreConstructionGeometry);

        const curveDataList: SketchTrimCurveData[] = [];
        curveCandidates.forEach((candidate) => {
            if (candidate.uuid === excludedCurveId) {
                return;
            }
            //------ 强力剪裁退化候选过滤 开始------
            // 候选边界可能来自构造线或求解刷新中的瞬时退化对象，不能让它中断当前目标的裁剪。
            const candidateCurveData = this.resolveTrimCandidateCurveData(candidate);
            if (!candidateCurveData) {
                return;
            }
            curveDataList.push(candidateCurveData);
            //------ 强力剪裁退化候选过滤 结束------
        });

        return curveDataList;
    }

    //------ 强力剪裁退化候选过滤 开始------
    /**
     * 安全解析强力剪裁候选曲线数据。
     *
     * 说明：
     * 1. 该方法只用于候选收集、拖拽命中这类“尚未确定直接操作对象”的场景；
     * 2. 目标曲线仍由 `applyPowerTrimOperation` 直接解析，保证真正被裁剪对象输出可定位日志与失败结果；
     * 3. 候选若是零长度线段或采样异常曲线，说明它不能提供有效裁剪边界或命中目标，应跳过而不是打断拖拽流程。
     *
     * @param candidate 候选曲线对象。
     * @returns 有效曲线数据；无效候选返回 null。
     */
    private resolveTrimCandidateCurveData(candidate: SketchTrimCurveObject): SketchTrimCurveData | null {
        if (this.isLineTrimCandidateLengthInvalid(candidate)) {
            return null;
        }

        const curveData = this.geometryService.resolveCurveData(candidate);
        if (curveData.samplePoints.length < 2) {
            console.error(`[SketchTrimController] 强力剪裁候选无有效采样点，曲线=${candidate.uuid}，采样点数量=${curveData.samplePoints.length}。`);
            return null;
        }
        const firstSample = curveData.samplePoints[0];
        const lastSample = curveData.samplePoints[curveData.samplePoints.length - 1];
        if (!firstSample || !lastSample) {
            console.error(`[SketchTrimController] 强力剪裁候选采样点索引异常，曲线=${candidate.uuid}。`);
            return null;
        }
        if (
            curveData.curveKind === SketchTrimCurveKind.Line
            && firstSample.position.distanceTo(lastSample.position) <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE
        ) {
            console.error(`[SketchTrimController] 强力剪裁候选线段退化，曲线=${candidate.uuid}。`);
            return null;
        }
        return curveData;
    }

    /**
     * 判断候选线段长度是否无效。
     *
     * 说明：
     * 1. 该判断只用于候选扫描路径，避免鼠标拖动时把无效候选反复送入几何解析并输出红色错误；
     * 2. 真正被裁剪的目标线仍由几何服务解析并输出可定位日志；
     * 3. 缺少端点协议的测试桩或异常对象不在这里吞掉，继续交给后续解析路径暴露问题。
     *
     * @param candidate 候选曲线。
     * @returns 无效线段返回 true。
     */
    private isLineTrimCandidateLengthInvalid(candidate: SketchTrimCurveObject): boolean {
        if (!isSketchLineObject(candidate)) {
            return false;
        }

        const start = candidate.startPoint?.getPosition?.();
        const end = candidate.endPoint?.getPosition?.();
        if (!start || !end || typeof start.distanceToSquared !== 'function') {
            return false;
        }

        const lengthSquared = start.distanceToSquared(end);
        return !Number.isFinite(lengthSquared)
            || lengthSquared <= SketchTrimGeometryService.LINEAR_LENGTH_TOLERANCE_SQUARED;
    }
    //------ 强力剪裁退化候选过滤 结束------

    /**
     * 更新线对象到单段结果。
     * 说明：
     * 1. 当保留段的起点/终点参数偏离原端（即被裁剪的一端），必须新建控制点替换端点引用；
     * 2. 直接调用 setPosition 会移动共享端点对象，导致相邻线段的端点也跟随移动（产生几何错误）；
     * 3. 只保留参数在原端（startT≈0 或 endT≈1）的端点引用，其余端一律新建控制点。
     * 4. 替换端点后，若旧端点不再有任何连接边，必须将其从场景中清除（避免孤立控制点残留）。
     * @param line 目标线。
     * @param segment 目标段。
     */
    private updateLineToSegment(line: SketchLineObject, segment: SketchTrimSegmentRange): void {
        const TOLERANCE = SketchTrimGeometryService.TOPOLOGY_TOLERANCE;

        // 判断哪一端需要新建控制点（偏离原端说明该端被裁掉，不能移动共享点）。
        const startNeedsNewPoint = segment.startT > TOLERANCE;
        const endNeedsNewPoint = segment.endT < 1 - TOLERANCE;

        // 若保留端本身是“中点复用点”且仍被其他边共享，则在 Trim 后需要断开共享，
        // 否则会把本次保留段重新并入相邻轮廓，出现“裁完又成闭环”的现象。
        const shouldDetachRetainedStart = !startNeedsNewPoint
            && this.shouldDetachRetainedTrimEndpoint(line.startPoint, line);
        const shouldDetachRetainedEnd = !endNeedsNewPoint
            && this.shouldDetachRetainedTrimEndpoint(line.endPoint, line);

        const container = line.parent;

        // 记录旧端点引用，用于替换后检查是否变成孤立点（如连续两次裁剪同一线时，第一次创建的端点会成为孤立点）。
        const oldStartPoint = (startNeedsNewPoint || shouldDetachRetainedStart)
            ? line.startPoint
            : null;
        const oldEndPoint = (endNeedsNewPoint || shouldDetachRetainedEnd)
            ? line.endPoint
            : null;

        // 为需要移动的端点解析控制点，线线裁剪落到已有线端点时必须复用该端点。
        let newStartPoint: SketchPointObject | undefined;
        let newEndPoint: SketchPointObject | undefined;

        if (startNeedsNewPoint) {
            // 裁剪边界若恰好落在其它线端点上，应直接复用该端点，保证线线拓扑真实连接。
            newStartPoint = this.findExistingLineEndpointAtPosition(line, segment.start)
                ?? this.createControlPoint(segment.start, line);
            this.transferOwnedDefaultMidPointExternalLineEndpoints(line, segment.start, newStartPoint);
        }
        if (shouldDetachRetainedStart) {
            newStartPoint = this.createControlPoint(segment.start, line);
        }
        if (endNeedsNewPoint) {
            // 同上：终点方向同样只复用其它草图线的端点，避免影响圆弧、样条等曲线拓扑。
            newEndPoint = this.findExistingLineEndpointAtPosition(line, segment.end)
                ?? this.createControlPoint(segment.end, line);
            this.transferOwnedDefaultMidPointExternalLineEndpoints(line, segment.end, newEndPoint);
        }
        if (shouldDetachRetainedEnd) {
            newEndPoint = this.createControlPoint(segment.end, line);
        }

        // 替换线端点引用（两端均可能同时需要替换）。
        if (newStartPoint && newEndPoint) {
            line.setEndpoints(newStartPoint, newEndPoint);
        } else if (newStartPoint) {
            line.setEndpoints(newStartPoint, line.endPoint);
        } else if (newEndPoint) {
            line.setEndpoints(line.startPoint, newEndPoint);
        }

        // 将新建控制点挂入场景；复用已有线端点时保持其原父级，避免破坏另一条线的拓扑层级。
        if (newStartPoint && container && !newStartPoint.parent) {
            this.viewer.addObject(newStartPoint, container);
            this.syncMaterialSnapshot(newStartPoint);
        }
        if (newEndPoint && container && !newEndPoint.parent) {
            this.viewer.addObject(newEndPoint, container);
            this.syncMaterialSnapshot(newEndPoint);
        }

        // 清理被替换后变成孤立点的旧端点（如首次裁剪后再次裁剪同一线，上次创建的中间端点需要清除）。
        if (oldStartPoint) {
            this.removeDetachedPoint(oldStartPoint);
        }
        if (oldEndPoint) {
            this.removeDetachedPoint(oldEndPoint);
        }

        line.updateGeometry();
    }

    /**
     * 判断保留端点是否需要在 Trim 后断开共享。
     * 说明：
     * 1. 仅对“中点复用为端点”的点执行断开，普通角点仍保持共享；
     * 2. 若该点仍被其他边共享，Trim 后继续共用会导致轮廓被重新闭合；
     * 3. 该规则只作用于单段保留更新路径，不影响双段拆分路径。
     * @param endpoint 待判定端点。
     * @param ownerLine 当前线段。
     * @returns 需要断开返回 true。
     */
    private shouldDetachRetainedTrimEndpoint(endpoint: SketchPointObject, ownerLine: SketchLineObject): boolean {
        if (!endpoint.isMidPoint) {
            return false;
        }

        return endpoint.getConnectedEdgesSnapshot().some((edge) => edge !== ownerLine);
    }

    /**
     * 查找位置与目标点吻合的其它草图线端点。
     *
     * 用途：强力裁剪线线相交时，裁剪边界若落在另一条线的端点上，
     * 必须复用该端点而不是新建重合点，否则后续闭合判定只能看到几何重合，看不到拓扑相连。
     * 合并距离必须使用集合闭合坐标兜底容差，不能使用拓扑参数容差，否则亚毫米矩形的多个角点会被误合并。
     *
     * @param line 目标线段。
     * @param position 裁剪边界位置（世界坐标）。
     * @returns 找到匹配点则返回，否则返回 undefined。
     */
    //------ 强力剪裁线线端点合并 开始------
    private findExistingLineEndpointAtPosition(
        line: SketchLineObject,
        position: THREE.Vector3,
    ): SketchPointObject | undefined {
        const TOLERANCE = SketchTrimGeometryService.ENDPOINT_MERGE_POSITION_TOLERANCE;

        const candidates = this.collectLineEndpointMergeCandidates(line);
        return candidates.find((point) => {
            if (point === line.startPoint || point === line.endPoint) {
                return false;
            }
            if (!this.isMergeableLineEndpoint(point, line)) {
                return false;
            }

            return point.getPosition().distanceTo(position) <= TOLERANCE;
        });
    }

    /**
     * 将目标线默认中点上的外部线端点迁移到真实控制点。
     *
     * 说明：
     * 1. 目标线自己的默认中点是派生辅助点，不能作为目标线裁剪后的业务端点；
     * 2. 若外部斜线端点复用了该默认中点，必须在目标线刷新几何前迁移；
     * 3. 迁移后默认中点继续由目标线维护，外部线则绑定到真实控制点。
     *
     * @param line 目标线。
     * @param position 裁剪边界位置。
     * @param replacementPoint 承接业务拓扑的真实控制点。
     */
    private transferOwnedDefaultMidPointExternalLineEndpoints(
        line: SketchLineObject,
        position: THREE.Vector3,
        replacementPoint: SketchPointObject,
    ): void {
        this.lineEndpointTransferService.transferOwnedDefaultMidPointExternalLineEndpoints({
            line,
            position,
            replacementPoint,
            positionTolerance: SketchTrimGeometryService.ENDPOINT_MERGE_POSITION_TOLERANCE,
        });
    }

    /**
     * 收集可参与线线端点合并的候选点。
     *
     * 说明：
     * 1. 先从目标线关联点中收集，覆盖“其它线端点被注册为目标线中间点”的历史拓扑；
     * 2. 再遍历活动草图组整棵对象树，覆盖端点位于集合、槽口降级后父级不同的场景；
     * 3. 使用 Map 按 uuid 去重，避免同一端点因多条路径重复参与匹配。
     *
     * @param line 被裁剪线。
     * @returns 候选端点列表。
     */
    private collectLineEndpointMergeCandidates(line: SketchLineObject): SketchPointObject[] {
        const pointMap = new Map<string, SketchPointObject>();

        line.getRelatedSketchPointsSnapshot().forEach((point) => {
            pointMap.set(point.uuid, point);
        });

        const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
        const searchRoot = activeSketchGroup ?? line.parent;
        searchRoot?.traverse((object) => {
            const runtimeObject = object as unknown as {isSketchPointObject?: boolean};
            if (runtimeObject.isSketchPointObject !== true) {
                return;
            }

            const point = object as unknown as SketchPointObject;
            pointMap.set(point.uuid, point);
        });

        return [...pointMap.values()];
    }

    /**
     * 判断点是否是可复用的其它草图线端点。
     *
     * 说明：
     * 1. 只接受端点角色，避免把普通草图点、圆心点、象限点误并入线段拓扑；
     * 2. 只接受连接到其它 SketchLineObject 的点，确保本次变更严格限定在线线裁剪；
     * 3. 同一参考面内才允许合并，避免跨草图或跨平面点位重合导致拓扑串线。
     *
     * @param point 候选点。
     * @param line 被裁剪线。
     * @returns 可以复用返回 true。
     */
    private isMergeableLineEndpoint(point: SketchPointObject, line: SketchLineObject): boolean {
        if (!point.isEndPoint) {
            return false;
        }

        if (this.lineEndpointTransferService.isOwnedDefaultMidPoint(line, point)) {
            return false;
        }

        if (point.referencePlane !== line.referencePlane) {
            return false;
        }

        return point.getConnectedEdgesSnapshot().some((edge) => edge !== line && isSketchLineObject(edge));
    }
    //------ 强力剪裁线线端点合并 结束------

    //------ 混合边裁剪闭合收口 开始------
    /**
     * 在 Trim 后尝试把真实闭合的混合边链收口为集合。
     *
     * 说明：
     * 1. 线段、圆裁剪生成的圆弧、圆弧裁剪后的保留段都应走同一闭合入口；
     * 2. 首尾相接不等于封闭，必须交给草图组统一闭合入口做完整闭合判定；
     * 3. 新建集合 ID 会并入当前 Trim 结果，让撤销历史把闭合收口与裁剪作为同一事务恢复。
     *
     * @param seedSources 本次 Trim 直接修改或创建的集合源边。
     * @returns 新建闭合集合 ID 列表。
     */
    private tryFinalizeClosedCollectionsAfterTrim(seedSources: readonly CollectSource[]): string[] {
        const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
        if (!activeSketchGroup) {
            return [];
        }

        const createdCollectionIds: string[] = [];
        const uniqueSeedSources = this.normalizeUniqueTrimClosureSources(seedSources);
        uniqueSeedSources.forEach((seedSource) => {
            // 每条受影响边单独作为入口尝试闭合，避免裁剪顺序影响混合边搜索结果。
            const collection = activeSketchGroup.tryFinalizeClosedSketchCollection(
                [seedSource],
                {syncContour: this.viewer.shadedSketchContoursEnabled},
            );
            if (collection) {
                createdCollectionIds.push(collection.uuid);
            }
        });

        return createdCollectionIds;
    }

    /**
     * 按 UUID 去重本次 Trim 影响的集合源边。
     * @param sources 原始集合源边列表。
     * @returns 去重后的集合源边列表。
     */
    private normalizeUniqueTrimClosureSources(sources: readonly CollectSource[]): CollectSource[] {
        const sourceMap = new Map<string, CollectSource>();
        sources.forEach((source) => {
            sourceMap.set(source.uuid, source);
        });
        return [...sourceMap.values()];
    }

    /**
     * 解析整段删除后可用于重新闭合收口的剩余种子边。
     *
     * 说明：
     * 1. Trim 删除闭合集合中的一条边后，原集合会变成开链；
     * 2. SOLIDWORKS 会基于当前草图拓扑重新识别可闭合轮廓，而不是只依赖被删除对象；
     * 3. 因此这里在删除前提取同集合剩余边，删除后交给统一闭合模块做拓扑解析。
     *
     * @param curve 即将被整段删除的曲线。
     * @returns 可用于闭合重算的剩余集合源边。
     */
    private resolveClosureSeedsAfterWholeCurveRemoval(curve: SketchTrimCurveObject): CollectSource[] {
        const parent = curve.parent;
        if (!isSketchCollectionObject(parent)) {
            return [];
        }
        return Array.from(parent.collectSet)
            .filter((source) => source.uuid !== curve.uuid);
    }

    //------ 混合边裁剪闭合收口 结束------

    /**
     * 将一条线拆分为两条。
     * @param line 目标线。
     * @param keptSegments 保留段。
     * @returns 拆分后的线对象列表，第一项为原线，第二项为新线。
     */
    private splitLineByKeptSegments(line: SketchLineObject, keptSegments: SketchTrimSegmentRange[]): SketchLineObject[] {
        if (keptSegments.length !== 2) {
            console.error(`[SketchTrimController] 拆分线段失败：保留段数量必须为 2，线段=${line.uuid}，保留段数量=${keptSegments.length}。`);
            return [line];
        }

        const container = line.parent;
        if (!container) {
            console.error(`[SketchTrimController] 拆分线段失败：目标线未挂载到场景，线段=${line.uuid}。`);
            return [line];
        }

        const first = keptSegments[0];
        const second = keptSegments[1];

        //------ 强力剪裁线线端点合并 开始------
        // 双段保留时两个断点同样可能落在其它草图线端点上，必须复用端点来保持线线共享拓扑。
        const firstSplitPoint = this.findExistingLineEndpointAtPosition(line, first.end)
            ?? this.createControlPoint(first.end, line);
        const secondSplitPoint = this.findExistingLineEndpointAtPosition(line, second.start)
            ?? this.createControlPoint(second.start, line);
        this.transferOwnedDefaultMidPointExternalLineEndpoints(line, first.end, firstSplitPoint);
        this.transferOwnedDefaultMidPointExternalLineEndpoints(line, second.start, secondSplitPoint);
        //------ 强力剪裁线线端点合并 结束------

        const originalStartPoint = line.startPoint;
        const originalEndPoint = line.endPoint;

        line.setEndpoints(originalStartPoint, firstSplitPoint);
        this.applyEndpointPosition(line, SketchTrimEndpointRole.Start, first.start);
        this.applyEndpointPosition(line, SketchTrimEndpointRole.End, first.end);
        line.updateGeometry();

        const secondLine = this.createLineFromTemplate(
            second.start,
            second.end,
            line,
            line.isConstruction,
            secondSplitPoint,
            originalEndPoint,
        );

        if (isSketchCollectionObject(container)) {
            if (!firstSplitPoint.parent) {
                this.viewer.addObject(firstSplitPoint, container);
            }
            if (!secondSplitPoint.parent) {
                this.viewer.addObject(secondSplitPoint, container);
            }
            this.replaceLineInCollection(container, line, [line, secondLine]);
        } else {
            if (!firstSplitPoint.parent) {
                this.viewer.addObject(firstSplitPoint, container);
            }
            if (!secondSplitPoint.parent) {
                this.viewer.addObject(secondSplitPoint, container);
            }
            this.viewer.addObject(secondLine, container);
        }

        this.syncMaterialSnapshot(line);
        this.syncMaterialSnapshot(secondLine);
        this.syncMaterialSnapshot(firstSplitPoint);
        this.syncMaterialSnapshot(secondSplitPoint);

        return [line, secondLine];
    }

    /**
     * 在集合中替换源线。
     * @param collection 集合对象。
     * @param target 目标线。
     * @param replacements 替换线列表。
     */
    private replaceLineInCollection(
        collection: SketchCollectionObject,
        target: SketchTrimCurveObject,
        replacements: SketchTrimCurveObject[],
    ): void {
        const wasClosedGeometry = collection.isClosedGeometry;
        const sources = Array.from(collection.collectSet);
        const index = sources.findIndex((source) => source.uuid === target.uuid);
        if (index < 0) {
            console.error(`[SketchTrimController] 集合替换失败：未找到目标曲线，集合=${collection.uuid}，曲线=${target.uuid}。`);
            return;
        }

        const nextSources = [
            ...sources.slice(0, index),
            ...replacements,
            ...sources.slice(index + 1),
        ];

        collection.setSources(nextSources as CollectSource[]);
        this.syncCollectionContourAfterTopologyMutation(collection, wasClosedGeometry);
    }

    /**
     * 在集合拓扑变更后同步轮廓显示。
     * @param collection 集合对象。
     * @param wasClosedGeometry 变更前是否为闭合几何。
     */
    private syncCollectionContourAfterTopologyMutation(
        collection: SketchCollectionObject,
        wasClosedGeometry: boolean,
    ): void {
        if (!this.viewer.shadedSketchContoursEnabled) {
            return;
        }

        if (collection.isClosedGeometry) {
            collection.syncContour();
            return;
        }

        if (wasClosedGeometry) {
            collection.clearContourDisplay();
        }
    }

    /**
     * 创建被剪段构造线备份。
     * @param baseLine 参考线。
     * @param trimmedSegment 被剪段。
     * @returns 新建线 ID。
     */
    private createConstructionBackupSegment(baseLine: SketchLineObject, trimmedSegment: SketchTrimSegmentRange): string {
        const startPoint = this.createControlPoint(trimmedSegment.start, baseLine);
        const endPoint = this.createControlPoint(trimmedSegment.end, baseLine);
        const backupLine = this.createLineFromTemplate(
            trimmedSegment.start,
            trimmedSegment.end,
            baseLine,
            true,
            startPoint,
            endPoint,
        );

        this.addObjectToSketchGroup(startPoint);
        this.addObjectToSketchGroup(endPoint);
        this.addObjectToSketchGroup(backupLine);

        this.syncMaterialSnapshot(startPoint);
        this.syncMaterialSnapshot(endPoint);
        this.syncMaterialSnapshot(backupLine);
        return backupLine.uuid;
    }

    /**
     * 捕获曲线在拓扑变化前的持久身份。
     *
     * @param curve 待变更曲线。
     * @returns 曲线持久 ID 与关联控制点对象快照。
     */
    private captureCurveIdentitySnapshot(curve: SketchTrimCurveObject): SketchTrimCurveIdentitySnapshot {
        return {
            entityId: curve.persistentId,
            points: [...curve.getRelatedSketchPointsSnapshot()],
        };
    }

    /**
     * 创建确定的点引用迁移记录。
     *
     * @param sourcePoint 变更前语义点。
     * @param targetPoint 变更后语义点。
     * @param targetCurve 变更后承载该点的曲线。
     * @returns 智能尺寸可直接消费的持久点映射。
     */
    private createPointTopologyMapping(
        sourcePoint: SketchPointObject,
        targetPoint: SketchPointObject,
        targetCurve: SketchTrimCurveObject,
    ): SketchDimensionTopologyChange['pointMappings'][number] {
        return {
            sourcePointId: sourcePoint.persistentId,
            targetPointId: targetPoint.persistentId,
            targetEntityId: targetCurve.persistentId,
        };
    }

    /**
     * 根据曲线变更前身份与规范继承目标创建拓扑变化。
     *
     * 只有命令能够明确证明的点语义才进入映射；没有映射且已从场景移除的点会显式标记为删除，
     * 尺寸迁移服务不会使用空间距离猜测新的引用目标。
     *
     * @param sourceIdentity 变更前身份快照。
     * @param targetCurve 规范尺寸继承曲线；源尺寸语义失效且没有兼容继承目标时为 null。
     * @param pointMappings 确定性点引用映射。
     * @param invalidatedDimensionKinds 目标实体仍存在但已不兼容的整实体尺寸类型。
     * @returns 单个源曲线的持久拓扑变化。
     */
    private createCurveTopologyChange(
        sourceIdentity: SketchTrimCurveIdentitySnapshot,
        targetCurve: SketchTrimCurveObject | null,
        pointMappings: SketchDimensionTopologyChange['pointMappings'] = [],
        invalidatedDimensionKinds: readonly SketchDimensionKind[] = [],
    ): SketchDimensionTopologyChange {
        const mappedSourcePointIds = new Set(
            pointMappings.map((mapping) => mapping.sourcePointId),
        );

        return {
            sourceEntityId: sourceIdentity.entityId,
            targetEntityId: targetCurve?.persistentId ?? null,
            pointMappings,
            deletedPointIds: sourceIdentity.points
                .filter((point) => !point.parent && !mappedSourcePointIds.has(point.persistentId))
                .map((point) => point.persistentId),
            ...(invalidatedDimensionKinds.length > 0 ? {invalidatedDimensionKinds} : {}),
        };
    }

    /**
     * 删除曲线并同步生成尺寸引用层需要的持久拓扑变化。
     *
     * @param curve 目标曲线。
     * @returns 运行时删除 ID 与持久拓扑变化。
     */
    private removeCurveEntityWithTopology(curve: SketchTrimCurveObject): SketchTrimCurveRemovalResult {
        const sourceIdentity = this.captureCurveIdentitySnapshot(curve);
        const removedEntityIds = this.removeCurveEntity(curve);

        if (removedEntityIds.length === 0) {
            return {
                removedEntityIds: [],
                topologyChanges: [],
            };
        }

        return {
            removedEntityIds,
            topologyChanges: [this.createCurveTopologyChange(sourceIdentity, null)],
        };
    }

    /**
     * 删除曲线实体并清理孤立相关点。
     * @param curve 目标曲线。
     * @returns 删除的实体 ID。
     */
    private removeCurveEntity(curve: SketchTrimCurveObject): string[] {
        const parent = curve.parent;

        if (!parent) {
            console.error(`[SketchTrimController] 删除曲线失败：目标曲线未挂载到场景，曲线=${curve.uuid}。`);
            return [];
        }

        //------ 强力裁剪约束清理 开始------
        // 删除整条曲线前同步清理约束，覆盖非线段曲线和直接删除入口，避免其它草图对象继续持有悬空关系。
        this.constraintCleanupService.cleanupCurveConstraints(
            curve,
            this.viewer.sketchGroupManager?.activeSketchGroup ?? null,
        );
        //------ 强力裁剪约束清理 结束------

        //------ 强力裁剪共享端点保留 开始------
        this.promoteSharedCurveControlPointsBeforeRemoval(curve, parent);
        //------ 强力裁剪共享端点保留 结束------

        //------ Trim 构造线圆删除兼容 开始------
        if (isSketchCollectionObject(parent)) {
            const isCollectionSource = Array.from(parent.collectSet)
                .some((source) => source.uuid === curve.uuid);

            if (isCollectionSource) {
                const wasClosedGeometry = parent.isClosedGeometry;
                const nextSources = Array.from(parent.collectSet)
                    .filter((source) => source.uuid !== curve.uuid);
                parent.setSources(nextSources as CollectSource[]);
                this.syncCollectionContourAfterTopologyMutation(parent, wasClosedGeometry);
            } else {
                // 多边形辅助圆等构造曲线可能直接挂在集合 children 下，但不属于 collectSet。
                // 这类对象删除时应按普通子节点移除，避免对集合源做无效重绑。
                parent.remove(curve);
            }
        } else {
            parent.remove(curve);
        }
        //------ Trim 构造线圆删除兼容 结束------

        curve.getRelatedSketchPointsSnapshot().forEach((point) => {
            point.unregisterConnectedEdge(curve);
            point.unregisterRelatedSketchObject(curve);
        });
        curve.getRelatedSketchPointsSnapshot().forEach((point) => {
            this.removeDetachedPoint(point);
        });
        return [curve.uuid];
    }

    //------ 强力裁剪共享端点保留 开始------
    /**
     * 删除曲线前提升仍被其它边引用的控制点。
     *
     * 说明：
     * 1. 旧闭合拓扑可能让线段共享的圆弧端点仍挂在圆弧子树中；
     * 2. 若直接删除圆弧，端点会随圆弧离开场景，剩余线段就失去可见端点；
     * 3. 因此删除前把仍连接其它边的曲线子点提升到曲线父级，保持剩余边的端点生命周期。
     *
     * @param curve 即将删除的曲线。
     * @param targetParent 曲线当前父级。
     */
    private promoteSharedCurveControlPointsBeforeRemoval(
        curve: SketchTrimCurveObject,
        targetParent: THREE.Object3D,
    ): void {
        curve.getRelatedSketchPointsSnapshot().forEach((point) => {
            // 只处理当前确实由待删曲线托管的点，避免改动已在集合层级的正常端点。
            if (point.parent !== curve) {
                return;
            }

            // 点若仍被其它边引用，就必须先离开待删曲线子树。
            if (!this.isCurveControlPointUsedByOtherEdge(point, curve)) {
                return;
            }

            this.viewer.addObject(point, targetParent);
            this.syncMaterialSnapshot(point);
        });
    }

    /**
     * 判断曲线控制点是否仍被其它边引用。
     *
     * @param point 控制点。
     * @param curve 当前即将删除的曲线。
     * @returns 仍被其它边引用返回 true。
     */
    private isCurveControlPointUsedByOtherEdge(point: SketchPointObject, curve: SketchTrimCurveObject): boolean {
        return point.getConnectedEdgesSnapshot().some((edge) => edge !== curve);
    }
    //------ 强力裁剪共享端点保留 结束------

    /**
     * 删除孤立点对象。
     * @param point 点对象。
     */
    private removeDetachedPoint(point: SketchPointObject): void {
        if (point.getConnectedEdgesSnapshot().length > 0) {
            return;
        }
        if (point.parent) {
            point.parent.remove(point);
        }
    }

    /**
     * 根据模板线创建新线。
     * @param start 起点。
     * @param end 终点。
     * @param templateLine 模板线。
     * @param isConstruction 是否构造线。
     * @param startPoint 起点对象。
     * @param endPoint 终点对象。
     * @returns 新线。
     */
    private createLineFromTemplate(
        start: THREE.Vector3,
        end: THREE.Vector3,
        templateLine: SketchLineObject,
        isConstruction: boolean,
        startPoint: SketchPointObject,
        endPoint: SketchPointObject,
    ): SketchLineObject {
        const material = isConstruction
            ? LineMaterialFactory.createConstructionLineMaterial()
            : LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default);

        const line = new SketchLineObject({
            startPoint,
            endPoint,
            material,
            referencePlane: templateLine.referencePlane,
            isConstruction,
            isInfiniteLength: templateLine.isInfiniteLength,
            baseStyle: isConstruction
                ? SketchEdgeBaseStyle.Construction
                : SketchEdgeBaseStyle.Basic,
        });

        this.applyEndpointPosition(line, SketchTrimEndpointRole.Start, start);
        this.applyEndpointPosition(line, SketchTrimEndpointRole.End, end);
        line.updateGeometry();
        if (line.material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(line.material)) {
            line.computeLineDistances();
        }

        return line;
    }

    /**
     * 创建控制点。
     * @param position 点位。
     * @param referenceLine 参考线。
     * @returns 控制点对象。
     */
    private createControlPoint(position: THREE.Vector3, referenceCurve: SketchTrimCurveObject): SketchPointObject {
        return this.createControlPointForPlane(position, referenceCurve.referencePlane);
    }

    /**
     * 根据参考面创建控制点。
     * @param position 点位。
     * @param referencePlane 参考面。
     * @returns 控制点对象。
     */
    private createControlPointForPlane(position: THREE.Vector3, referencePlane: SketchTrimCurveObject['referencePlane']): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });
    }

    /**
     * 写入线端点位置。
     * @param line 目标线。
     * @param endpointRole 端点角色。
     * @param position 目标位置。
     */
    private applyEndpointPosition(
        line: SketchLineObject,
        endpointRole: SketchTrimEndpointRole,
        position: THREE.Vector3,
    ): void {
        if (endpointRole === SketchTrimEndpointRole.Start) {
            line.startPoint.setPosition(position.clone());
            return;
        }
        line.endPoint.setPosition(position.clone());
    }

    /**
     * 将对象加入当前草图组。
     * @param object 目标对象。
     */
    private addObjectToSketchGroup(object: THREE.Object3D): void {
        if (object.parent) {
            return;
        }
        this.viewer.addSketchObject(object);
    }

    /**
     * 同步对象材质快照。
     * @param object 目标对象。
     */
    private syncMaterialSnapshot(object: THREE.Object3D): void {
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(object);
    }

    /**
     * 解析点击点。
     * @param lineData 线数据。
     * @param hitPoint 命中点。
     * @returns 三维点。
     */
    private resolvePickPoint(lineData: SketchTrimLineData, hitPoint?: [number, number, number]): THREE.Vector3 {
        if (hitPoint && hitPoint.length >= 3) {
            return new THREE.Vector3(hitPoint[0], hitPoint[1], hitPoint[2]);
        }
        return lineData.start.clone().add(lineData.end).multiplyScalar(0.5);
    }

    /**
     * 解析曲线点击点。
     * @param curveData 曲线数据。
     * @param hitPoint 命中点。
     * @returns 三维点。
     */
    private resolveCurvePickPoint(curveData: SketchTrimCurveData, hitPoint?: [number, number, number]): THREE.Vector3 {
        if (hitPoint && hitPoint.length >= 3) {
            return new THREE.Vector3(hitPoint[0], hitPoint[1], hitPoint[2]);
        }

        const middleIndex = Math.floor((curveData.samplePoints.length - 1) / 2);
        return curveData.samplePoints[middleIndex]?.position.clone()
            ?? new THREE.Vector3();
    }

    /**
     * 解析曲线类型。
     * @param curve 曲线对象。
     * @returns 曲线类型枚举。
     */
    private resolveCurveKind(curve: SketchTrimCurveObject): SketchTrimCurveKind {
        if (isSketchLineObject(curve)) {
            return SketchTrimCurveKind.Line;
        }
        if (isSketchArcObject(curve)) {
            return SketchTrimCurveKind.Arc;
        }
        if (isSketchCircleObject(curve)) {
            return SketchTrimCurveKind.Circle;
        }
        //------ 样条精确裁剪 开始------
        if (isSketchParabolaObject(curve)) {
            return SketchTrimCurveKind.Parabola;
        }
        if (isSketchSplineObject(curve)) {
            return SketchTrimCurveKind.Spline;
        }
        if (isSketchStyleSplineObject(curve)) {
            return SketchTrimCurveKind.StyleSpline;
        }
        //------ 样条精确裁剪 结束------
        return SketchTrimCurveKind.Ellipse;
    }

    /**
     * 按 ID 解析强力剪裁曲线对象。
     * @param objectId 对象 ID。
     * @returns 曲线对象；未命中时返回 null。
     */
    private resolvePowerTrimCurveById(objectId: string): SketchTrimCurveObject | null {
        const object = this.viewer.scene.getObjectByProperty('uuid', objectId);
        if (!object) {
            return null;
        }
        return this.isInsideOutsideCurveObject(object)
            ? object
            : null;
    }

    /**
     * 判断场景对象是否属于通用 Trim 曲线矩阵。
     *
     * 该规则被 Power Trim、TrimToClosest 与 Inside/Outside 共用，集中维护实体类型白名单，
     * 避免新增曲线时选择层、对象解析层和几何层出现多套不一致判断。
     *
     * @param object 待判断场景对象。
     * @returns 可参与通用曲线裁剪时返回 true。
     */
    private isInsideOutsideCurveObject(object: THREE.Object3D): object is SketchTrimCurveObject {
        if (this.isStyleSplineAuxiliaryLine(object)) {
            return false;
        }
        return isSketchLineObject(object)
            || isSketchArcObject(object)
            || isSketchCircleObject(object)
            || isSketchEllipseObject(object)
            || isSketchParabolaObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object);
    }

    /**
     * 解析强力剪裁目标曲线对象。
     * @param entity 实体引用。
     * @returns 曲线对象。
     */
    private resolvePowerTrimTarget(entity: SketchTrimEntityRef): SketchTrimCurveObject | null {
        const object = this.resolvePowerTrimCurveById(entity.objectId);
        if (!object) {
            console.error(`[SketchTrimController] 未找到可操作曲线：${entity.objectId}。`);
            return null;
        }
        if (this.options.ignoreConstructionGeometry && object.isConstruction) {
            console.error(`[SketchTrimController] 当前已启用“忽略构造几何”，命中实体不可操作，曲线=${object.uuid}。`);
            return null;
        }
        return object;
    }

    /**
     * 解析目标线对象。
     * @param entity 实体引用。
     * @returns 线对象。
     */
    private resolveTargetLine(entity: SketchTrimEntityRef): SketchLineObject | null {
        const object = this.viewer.scene.getObjectByProperty('uuid', entity.objectId);
        if (!object || !isSketchLineObject(object)) {
            console.error(`[SketchTrimController] 未找到可操作线段：${entity.objectId}。`);
            return null;
        }
        if (this.options.ignoreConstructionGeometry && object.isConstruction) {
            console.error(`[SketchTrimController] 当前已启用“忽略构造几何”，命中实体不可操作，线段=${object.uuid}。`);
            return null;
        }
        return object;
    }

    /**
     * 解析 Corner 目标开放曲线。
     *
     * @param entity 实体引用。
     * @returns 支持自然角点计算的开放曲线。
     */
    private resolveTargetCornerCurve(entity: SketchTrimEntityRef): SketchTrimCurveObject | null {
        const object = this.viewer.scene.getObjectByProperty('uuid', entity.objectId);
        if (!object || !this.cornerGeometryService.isSupportedOpenCurveObject(object)) {
            console.error(`[SketchTrimController] 未找到可操作开放曲线：${entity.objectId}。`);
            return null;
        }
        return object;
    }

    /**
     * 判断两个剪裁实体引用是否指向同一条线段。
     * @param left 左侧实体。
     * @param right 右侧实体。
     * @returns 若为同一实体返回 true。
     */
    private isSameTrimEntity(
        left: Pick<SketchTrimEntityRef, 'objectId' | 'segmentIndex'>,
        right: Pick<SketchTrimEntityRef, 'objectId' | 'segmentIndex'>,
    ): boolean {
        return left.objectId === right.objectId
            && (left.segmentIndex ?? null) === (right.segmentIndex ?? null);
    }

    /**
     * 按线段拖拽路径采样拾取。
     * @param fromClientX 起点 X。
     * @param fromClientY 起点 Y。
     * @param toClientX 终点 X。
     * @param toClientY 终点 Y。
     * @param ignoreConstructionGeometry 是否忽略构造线。
     * @returns 命中实体列表。
     */
    private pickSketchTrimEntitiesAlongClientSegment(
        mode: SketchTrimMode,
        fromClientX: number,
        fromClientY: number,
        toClientX: number,
        toClientY: number,
        ignoreConstructionGeometry: boolean,
    ): SketchTrimEntityRef[] {
        //------ 强力剪裁拖拽整段精确命中 开始------
        // 拖拽路径改为整段精确求交：
        // 1. 先把屏幕起终点投影到候选曲线所在参考面；
        // 2. 再用几何服务直接求“拖拽线段 vs 曲线”的真实交点；
        // 3. 最后按拖拽方向排序，保持连续修剪顺序稳定。
        const hitEntities: SketchTrimEntityRef[] = [];
        const hitCandidates: Array<{entity: SketchTrimEntityRef; sourceT: number}> = [];
        const seen = new Set<string>();
        const shouldCollectCurveTargets = mode === SketchTrimMode.Power
            || (
                mode === SketchTrimMode.TrimToClosest
                && this.options.singleEntityBehavior === SketchTrimSingleEntityBehavior.Trim
            );
        const candidates = shouldCollectCurveTargets
            ? this.collectPowerTrimCurveCandidates(ignoreConstructionGeometry)
            : this.collectLineCandidates(ignoreConstructionGeometry);

        candidates.forEach((candidate) => {
            const dragSegment = this.resolveClientSegmentOnReferencePlane(
                fromClientX,
                fromClientY,
                toClientX,
                toClientY,
                candidate.referencePlane,
            );
            if (!dragSegment) {
                return;
            }

            //------ 强力剪裁退化线段防护 开始------
            // 拖拽整段命中会扫描全场候选，历史恢复后的退化线不能让本次鼠标移动中断。
            const curveData = this.resolveTrimCandidateCurveData(candidate);
            if (!curveData) {
                return;
            }
            //------ 强力剪裁退化线段防护 结束------
            const intersections = this.geometryService.collectCurveIntersectionsWithSegment(
                curveData,
                dragSegment.startWorld,
                dragSegment.endWorld,
            );

            intersections.forEach((intersection) => {
                hitCandidates.push({
                    sourceT: intersection.sourceT,
                    entity: {
                        objectId: candidate.uuid,
                        segmentIndex: null,
                        hitPoint: [intersection.point.x, intersection.point.y, intersection.point.z],
                    },
                });
            });
        });

        hitCandidates.sort((left, right) => left.sourceT - right.sourceT);

        hitCandidates.forEach(({entity}) => {
            const key = `${entity.objectId}:${entity.segmentIndex ?? 'null'}`;
            if (seen.has(key)) {
                return;
            }

            seen.add(key);
            hitEntities.push(entity);
        });
        //------ 强力剪裁拖拽整段精确命中 结束------

        return hitEntities;
    }

    /**
     * 将屏幕拖拽起终点投影到指定参考面。
     * @param fromClientX 起点屏幕 X。
     * @param fromClientY 起点屏幕 Y。
     * @param toClientX 终点屏幕 X。
     * @param toClientY 终点屏幕 Y。
     * @param referencePlane 目标参考面。
     * @returns 参考面上的世界线段；若投影失败返回 null。
     */
    private resolveClientSegmentOnReferencePlane(
        fromClientX: number,
        fromClientY: number,
        toClientX: number,
        toClientY: number,
        referencePlane: SketchTrimCurveObject['referencePlane'],
    ): {startWorld: THREE.Vector3; endWorld: THREE.Vector3} | null {
        const startWorld = this.resolveClientPointOnReferencePlane(fromClientX, fromClientY, referencePlane);
        const endWorld = this.resolveClientPointOnReferencePlane(toClientX, toClientY, referencePlane);
        if (!startWorld || !endWorld) {
            return null;
        }

        return {
            startWorld,
            endWorld,
        };
    }

    /**
     * 将单个屏幕坐标投影到指定参考面。
     * @param clientX 屏幕 X。
     * @param clientY 屏幕 Y。
     * @param referencePlane 目标参考面。
     * @returns 参考面世界点；若射线与平面无交返回 null。
     */
    private resolveClientPointOnReferencePlane(
        clientX: number,
        clientY: number,
        referencePlane: SketchTrimCurveObject['referencePlane'],
    ): THREE.Vector3 | null {
        const camera = this.viewer.camera;
        const container = this.viewer.container;
        if (!camera || !container) {
            console.error(`[SketchTrimController] 拖拽路径投影失败：相机或容器不可用。`);
            return null;
        }

        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouseNdc, camera);
        return this.raycaster.ray.intersectPlane(referencePlane, new THREE.Vector3());
    }

    /**
     * 在指定屏幕坐标拾取线段。
     * @param clientX 客户端 X。
     * @param clientY 客户端 Y。
     * @param ignoreConstructionGeometry 是否忽略构造线。
     * @returns 命中结果。
     */
    private pickLineAtClientPoint(
        clientX: number,
        clientY: number,
        ignoreConstructionGeometry: boolean,
    ): SketchTrimPickResult | null {
        const camera = this.viewer.camera;
        const container = this.viewer.container;
        if (!camera || !container) {
            console.error(`[SketchTrimController] 拾取失败：相机或容器不可用。`);
            return null;
        }

        const candidates = this.collectLineCandidates(ignoreConstructionGeometry);
        if (candidates.length === 0) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouseNdc, camera);
        this.raycaster.params.Line.threshold = SketchTrimController.LINE_PICK_THRESHOLD;

        const intersects = this.raycaster.intersectObjects(candidates, true)
            .map((intersect) => {
                const ownerLine = this.findAncestorLine(intersect.object);
                if (!ownerLine) {
                    return null;
                }

                return {
                    line: ownerLine,
                    point: intersect.point,
                    distance: intersect.distance,
                    distanceToRay: Number.isFinite(intersect.distanceToRay)
                        ? intersect.distanceToRay
                        : Number.POSITIVE_INFINITY,
                };
            })
            .filter((item): item is {line: SketchLineObject; point: THREE.Vector3; distance: number; distanceToRay: number} => item !== null)
            .sort((left, right) => {
                if (Math.abs(left.distanceToRay - right.distanceToRay) > 1e-7) {
                    return left.distanceToRay - right.distanceToRay;
                }
                return left.distance - right.distance;
            });

        const picked = intersects[0] ?? null;
        if (!picked) {
            return null;
        }

        return {
            object: picked.line,
            objectId: picked.line.uuid,
            segmentIndex: null,
            hitPoint: [picked.point.x, picked.point.y, picked.point.z],
        };
    }

    /**
     * 在指定屏幕坐标拾取可强力剪裁曲线。
     * @param clientX 客户端 X。
     * @param clientY 客户端 Y。
     * @param ignoreConstructionGeometry 是否忽略构造线。
     * @returns 命中结果。
     */
    private pickTrimCurveAtClientPoint(
        clientX: number,
        clientY: number,
        ignoreConstructionGeometry: boolean,
    ): SketchTrimPickResult | null {
        const camera = this.viewer.camera;
        const container = this.viewer.container;
        if (!camera || !container) {
            console.error(`[SketchTrimController] 拾取失败：相机或容器不可用。`);
            return null;
        }

        const candidates = this.collectPowerTrimCurveCandidates(ignoreConstructionGeometry);
        if (candidates.length === 0) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouseNdc, camera);
        // 正式曲线本体按屏幕距离裁决，不递归拾取其控制点或辅助子对象。
        // Line 的原生命中不保证提供 distanceToRay，同平面射线深度也不能表示鼠标距离。
        candidates.forEach(candidate => candidate.updateWorldMatrix(true, false));
        const picked = this.screenSpacePicker.pickNearestLine({
            lines: candidates,
            camera,
            containerRect: rect,
            clientX,
            clientY,
            ray: this.raycaster.ray,
        });
        if (!picked) {
            return null;
        }

        const curve = this.findAncestorTrimCurve(picked.object);
        if (!curve) {
            return null;
        }

        return {
            object: curve,
            objectId: curve.uuid,
            segmentIndex: null,
            hitPoint: [picked.point.x, picked.point.y, picked.point.z],
        };
    }

    /**
     * 收集可拾取线候选。
     * @param ignoreConstructionGeometry 是否忽略构造线。
     * @returns 线对象列表。
     */
    private collectLineCandidates(ignoreConstructionGeometry: boolean): SketchLineObject[] {
        const candidates: SketchLineObject[] = [];
        this.viewer.scene.traverse((object) => {
            if (!isSketchLineObject(object)) {
                return;
            }
            if (this.shouldExcludeTrimCandidate(object)) {
                return;
            }
            if (ignoreConstructionGeometry && object.isConstruction) {
                return;
            }
            if (!this.isObjectActuallyVisible(object)) {
                return;
            }
            candidates.push(object);
        });
        return candidates;
    }

    /**
     * 收集强力剪裁可拾取曲线候选。
     * @param ignoreConstructionGeometry 是否忽略构造线。
     * @returns 曲线对象列表。
     */
    private collectPowerTrimCurveCandidates(ignoreConstructionGeometry: boolean): SketchTrimCurveObject[] {
        const candidates: SketchTrimCurveObject[] = [];
        this.viewer.scene.traverse((object) => {
            if (this.serviceIsTrimCurveSelectableEntity(object, ignoreConstructionGeometry)) {
                candidates.push(object);
            }
        });
        return candidates;
    }

    /**
     * 向上查找线对象祖先。
     * @param object 当前对象。
     * @returns 线对象。
     */
    private findAncestorLine(object: THREE.Object3D): SketchLineObject | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (isSketchLineObject(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 向上查找强力剪裁曲线对象祖先。
     * @param object 当前对象。
     * @returns 曲线对象。
     */
    private findAncestorTrimCurve(object: THREE.Object3D): SketchTrimCurveObject | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (this.isStyleSplineAuxiliaryLine(current)) {
                return null;
            }
            if (
                isSketchLineObject(current)
                || isSketchArcObject(current)
                || isSketchCircleObject(current)
                || isSketchEllipseObject(current)
                //------ 样条精确裁剪 开始------
                || isSketchParabolaObject(current)
                || isSketchSplineObject(current)
                || isSketchStyleSplineObject(current)
                //------ 样条精确裁剪 结束------
            ) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 判断对象是否为样式样条辅助线。
     * 说明：
     * 1. 样式样条辅助线只用于显示控制折线，不参与正式裁剪；
     * 2. 强力裁剪若命中这类辅助线，必须在进入采集结果前直接丢弃；
     * 3. 这里要求父对象是样式样条，避免误伤其它辅助对象。
     * @param object 目标对象。
     * @returns 是否为样式样条辅助线。
     */
    private isStyleSplineAuxiliaryLine(object: THREE.Object3D): boolean {
        return isSketchLineObject(object)
            && object.isAuxiliaryObject
            && object.parent !== null
            && isSketchStyleSplineObject(object.parent);
    }

    /**
     * 判断候选对象是否应从 Trim 工作流中排除。
     * 说明：
     * 1. 辅助对象只服务局部交互/预览，不应进入业务裁剪求交；
     * 2. 无限线属于绘制/约束辅助表达，进入最近交点计算会产生错误边界；
     * 3. 绘制预览线与预览圆弧只能用于视觉反馈，不能参与拾取与修剪。
     * @param object 候选对象。
     * @returns 若应排除返回 true。
     */
    private shouldExcludeTrimCandidate(object: SketchTrimCurveObject): boolean {
        if (isSketchLineObject(object)) {
            return object.isAuxiliaryObject
                || object.isInfiniteLength
                || object.isDrawingPreviewLine
                || this.isLineTrimCandidateLengthInvalid(object);
        }
        if (isSketchArcObject(object)) {
            return object.isDrawingPreviewArc;
        }
        //------ 样条精确裁剪 开始------
        if (isSketchParabolaObject(object) || isSketchSplineObject(object) || isSketchStyleSplineObject(object)) {
            return false;
        }
        //------ 样条精确裁剪 结束------
        return false;
    }

    /**
     * 判断对象是否实际可见。
     * @param object 目标对象。
     * @returns 是否可见。
     */
    private isObjectActuallyVisible(object: THREE.Object3D): boolean {
        if (!object.visible) {
            return false;
        }
        let current = object.parent;
        while (current) {
            if (!current.visible) {
                return false;
            }
            current = current.parent;
        }
        return true;
    }

    /**
     * 发布操作结果消息。
     * @param result 操作结果。
     */
    private publishResult(result: SketchTrimOperationResult): void {
        if (result.success) {
            this.viewer.drawingNoticeCallback?.(result.message, 'info');
            return;
        }
        this.viewer.drawingNoticeCallback?.(result.message, 'warning');
    }

    /**
     * 解析模式显示文案。
     * @param mode 模式。
     * @returns 文案。
     */
    private resolveModeDisplayName(mode: SketchTrimMode): string {
        return this.modeServices[mode].getDisplayName();
    }

    /**
     * 重置瞬时状态。
     */
    private resetTransientState(): void {
        this.lastSelectedEntity = null;
        this.clearPowerUndoState();
        this.resetAllModeServiceStates();
    }

    /**
     * 依据模式重置顺序选择态。
     */
    private resetSelectionStateByMode(): void {
        this.lastSelectedEntity = null;
        this.resetAllModeServiceStates();
    }

    /**
     * 重置全部模式服务私有状态。
     */
    private resetAllModeServiceStates(): void {
        Object.values(this.modeServices).forEach((service) => {
            service.resetState();
        });
    }

    /**
     * 清理强力剪裁撤销态。
     */
    private clearPowerUndoState(): void {
        this.getPowerModeService().clearPowerUndoState();
    }
}
